import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api, formatDate } from '../utils/api'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { OrderDetailModal } from './shared'
import DealDetailModal from './deals/DealDetailModal'
import '../styles/pages.css'

/**
 * ResizeHandle - Draggable divider for resizing panels
 */
function ResizeHandle({ onResize, direction = 'horizontal' }) {
  const handleRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isHovering, setIsHovering] = useState(false)

  const handleMouseDown = useCallback((e) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e) => {
      onResize(direction === 'horizontal' ? e.clientX : e.clientY)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, onResize, direction])

  const isActive = isDragging || isHovering

  return (
    <div
      ref={handleRef}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        width: direction === 'horizontal' ? 12 : '100%',
        height: direction === 'horizontal' ? '100%' : 12,
        background: 'transparent',
        cursor: direction === 'horizontal' ? 'col-resize' : 'row-resize',
        flexShrink: 0,
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Visual indicator line */}
      <div style={{
        width: direction === 'horizontal' ? 4 : '100%',
        height: direction === 'horizontal' ? '100%' : 4,
        background: isActive ? 'var(--primary)' : 'rgba(0,0,0,0.08)',
        borderRadius: 2,
        transition: 'background 0.15s, transform 0.15s',
        transform: isActive ? 'scale(1.2)' : 'scale(1)'
      }} />
      {/* Grip dots */}
      <div style={{
        position: 'absolute',
        display: 'flex',
        flexDirection: direction === 'horizontal' ? 'column' : 'row',
        gap: 3,
        opacity: isActive ? 1 : 0.4,
        transition: 'opacity 0.15s'
      }}>
        {[0, 1, 2].map(i => (
          <div
            key={i}
            style={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              background: isActive ? 'var(--primary)' : 'rgba(0,0,0,0.3)'
            }}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * EmailContent - Renders HTML email content safely
 * Prefers body_html over body_text for rich formatting
 */
function EmailContent({ html, text, fallback }) {
  const content = html || text || fallback

  // If we have HTML content, render it
  if (html) {
    // Sanitize email HTML to prevent layout breakage
    let sanitizedHtml = html
      // Strip <script> tags - email scripts can modify the page DOM and break layout
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // Strip <style> tags - email global styles (*, body) leak into the app
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
      // Strip <link rel="stylesheet"> tags - external stylesheets can affect the app
      .replace(/<link[^>]*rel=["']stylesheet["'][^>]*>/gi, '')
    return (
      <div
        className="email-content"
        style={{
          fontFamily: 'inherit',
          lineHeight: 1.5,
          wordBreak: 'break-word'
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    )
  }

  // Otherwise render plain text with whitespace preserved
  return (
    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
      {content}
    </pre>
  )
}

/**
 * AttachmentThumbnail - Displays email attachment with click to view
 */
function AttachmentThumbnail({ ticketId, attachment }) {
  const [loading, setLoading] = useState(false)
  const [imageData, setImageData] = useState(null)
  const [showModal, setShowModal] = useState(false)

  const isImage = attachment.mimeType?.startsWith('image/')

  async function loadAttachment() {
    if (!attachment.attachmentId) return
    setLoading(true)
    try {
      const response = await api.get(`/api/tickets/${ticketId}/attachment/${attachment.attachmentId}`)
      if (response.data) {
        setImageData(response.data)
        if (isImage) {
          setShowModal(true)
        } else {
          // For non-images, trigger download
          const link = document.createElement('a')
          link.href = response.data
          link.download = attachment.filename
          link.click()
        }
      }
    } catch (error) {
      console.error('Failed to load attachment:', error)
      alert('Failed to load attachment')
    } finally {
      setLoading(false)
    }
  }

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <>
      <div
        onClick={loadAttachment}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: '#f3f4f6',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 12,
          maxWidth: 200,
          overflow: 'hidden'
        }}
        title={attachment.filename}
      >
        <span style={{ fontSize: 16 }}>
          {isImage ? '🖼️' : attachment.mimeType?.includes('pdf') ? '📄' : '📎'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
            {loading ? 'Loading...' : attachment.filename}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 10 }}>
            {formatSize(attachment.size)}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showModal && imageData && (
        <div
          className="modalOverlay"
          onClick={() => setShowModal(false)}
          style={{ zIndex: 1001 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: 'white',
              borderRadius: 8,
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <div style={{
              padding: '8px 16px',
              borderBottom: '1px solid rgba(0,0,0,.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontWeight: 500, fontSize: 14 }}>{attachment.filename}</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <img
              src={imageData}
              alt={attachment.filename}
              style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 50px)', display: 'block' }}
            />
          </div>
        </div>
      )}
    </>
  )
}

/**
 * DownloadAllAttachments - Downloads all attachments as a zip file
 */
function DownloadAllAttachments({ ticketId, attachments }) {
  const [downloading, setDownloading] = useState(false)
  const [progress, setProgress] = useState('')

  async function downloadAll() {
    const downloadable = attachments.filter(a => a.attachmentId)
    if (!downloadable.length) return

    setDownloading(true)
    setProgress(`0/${downloadable.length}`)

    try {
      const zip = new JSZip()
      const usedNames = new Set()

      for (let i = 0; i < downloadable.length; i++) {
        setProgress(`${i + 1}/${downloadable.length}`)
        try {
          const response = await api.get(`/api/tickets/${ticketId}/attachment/${downloadable[i].attachmentId}`)
          if (response.data) {
            // Convert data URL to binary
            const base64 = response.data.split(',')[1]
            // Deduplicate filenames
            let name = downloadable[i].filename || `attachment_${i + 1}`
            if (usedNames.has(name)) {
              const ext = name.lastIndexOf('.') > 0 ? name.substring(name.lastIndexOf('.')) : ''
              const base = ext ? name.substring(0, name.lastIndexOf('.')) : name
              let n = 2
              while (usedNames.has(`${base}_${n}${ext}`)) n++
              name = `${base}_${n}${ext}`
            }
            usedNames.add(name)
            zip.file(name, base64, { base64: true })
          }
        } catch (err) {
          console.error(`Failed to fetch attachment ${i + 1}:`, err)
        }
      }

      const blob = await zip.generateAsync({ type: 'blob' })
      saveAs(blob, `attachments_${ticketId}.zip`)
    } catch (error) {
      console.error('Failed to create zip:', error)
      alert('Failed to download attachments')
    } finally {
      setDownloading(false)
      setProgress('')
    }
  }

  return (
    <button
      onClick={downloadAll}
      disabled={downloading}
      style={{
        background: 'none',
        border: 'none',
        color: 'var(--primary)',
        cursor: downloading ? 'wait' : 'pointer',
        fontSize: 11,
        fontWeight: 500,
        padding: '2px 6px',
        borderRadius: 4
      }}
      title="Download all attachments as zip"
    >
      {downloading ? `Downloading ${progress}...` : 'Download All'}
    </button>
  )
}

/**
 * CustomerModal - Shows customer activity, deals, and orders for an email
 */
function CustomerModal({ email, onClose }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [itemDetails, setItemDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)

  useEffect(() => {
    if (email) {
      loadCustomerData()
    }
  }, [email])

  async function loadCustomerData() {
    setLoading(true)
    try {
      const response = await api.get(`/api/tickets/customer/${encodeURIComponent(email)}`)
      setData(response)
    } catch (error) {
      console.error('Failed to load customer data:', error)
    } finally {
      setLoading(false)
    }
  }

  async function selectItem(type, id, basicData) {
    setSelectedItem({ type, id, data: basicData })
    setItemDetails(null)
    setLoadingDetails(true)

    try {
      if (type === 'deal') {
        const response = await api.get(`/api/deals/${id}`)
        setItemDetails(response.deal || response)
      } else if (type === 'order') {
        // Use shopifyId if available for Shopify orders, otherwise fallback
        const shopifyId = basicData?.shopifyId || id
        const response = await api.get(`/api/orders/shopify/${shopifyId}`)
        setItemDetails(response.order || response)
      } else if (type === 'ticket') {
        const response = await api.get(`/api/tickets/${id}`)
        setItemDetails(response.ticket || response)
      }
    } catch (error) {
      console.error(`Failed to load ${type} details:`, error)
      setItemDetails(basicData) // Fall back to basic data
    } finally {
      setLoadingDetails(false)
    }
  }

  if (!email) return null

  const stageLabels = {
    'appointmentscheduled': 'Quote Requested',
    '4383256790': 'Quote Ready',
    'qualifiedtobuy': 'Quote Sent',
    '2136864980': 'Warehouse Drop Off',
    '597442256': 'Awaiting Shipping Label',
    '4367683815': 'Awaiting Collection',
    'presentationscheduled': 'Shipping Arranged',
    '835164638': 'Arrived To Test',
    'contractsent': 'Awaiting Payment',
    'closedwon': 'Closed Traded',
    'closedlost': 'Closed Lost',
    '589630139': 'Quote Chased',
    '589142218': 'Problem With Shipping',
    '588059587': 'Issue with Item',
    '609520315': 'Listing Service Offered',
    '853396936': 'Listing Service Listing',
    '638945017': 'Last Chance Offer',
    '901659893': 'More Info Needed',
    '1616888039': 'Duplicate Trade Request',
    '4311086309': 'Artifacts'
  }

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '85vh' }}>
        <div className="modalHead">
          <h2>Customer: {email}</h2>
          <button className="modalClose" onClick={onClose}>&times;</button>
        </div>
        <div className="modalBody" style={{ padding: 0, overflow: 'auto', maxHeight: 'calc(85vh - 60px)' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className="loading-spinner" />
            </div>
          ) : (
            <div>
              {/* Tickets */}
              <div style={{ borderBottom: '1px solid rgba(0,0,0,.08)' }}>
                <div style={{ padding: '12px 20px', background: 'var(--bg)', fontWeight: 600, fontSize: 13 }}>
                  Tickets ({data?.tickets?.length || 0})
                </div>
                {data?.tickets?.length > 0 ? (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.tickets.map(ticket => (
                      <div
                        key={ticket.id}
                        style={{
                          padding: '10px 20px',
                          borderBottom: '1px solid rgba(0,0,0,.04)',
                          cursor: 'pointer',
                          background: selectedItem?.type === 'ticket' && selectedItem?.id === ticket.id ? 'var(--bg)' : 'white'
                        }}
                        onClick={() => selectItem('ticket', ticket.id, ticket)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 500 }}>{ticket.subject || '(No subject)'}</span>
                          <span className={`badge ${(ticket.status === 'new' || ticket.status === 'awaiting_reply') ? 'badge-success' : ticket.status === 'resolved' ? '' : 'badge-secondary'}`}>
                            {ticket.status}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                          {formatDate(ticket.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 20, color: 'var(--muted)', textAlign: 'center' }}>No tickets found</div>
                )}
              </div>

              {/* Deals */}
              <div style={{ borderBottom: '1px solid rgba(0,0,0,.08)' }}>
                <div style={{ padding: '12px 20px', background: 'var(--bg)', fontWeight: 600, fontSize: 13 }}>
                  Deals ({data?.deals?.length || 0})
                </div>
                {data?.deals?.length > 0 ? (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.deals.map(deal => (
                      <div
                        key={deal.id}
                        style={{
                          padding: '10px 20px',
                          borderBottom: '1px solid rgba(0,0,0,.04)',
                          cursor: 'pointer',
                          background: selectedItem?.type === 'deal' && selectedItem?.id === deal.id ? 'var(--bg)' : 'white'
                        }}
                        onClick={() => selectItem('deal', deal.id, deal)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 500 }}>{deal.name}</span>
                          {deal.amount && <span style={{ fontWeight: 600 }}>£{parseFloat(deal.amount).toFixed(2)}</span>}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                          {stageLabels[deal.stage] || deal.stage} • {formatDate(deal.createdAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 20, color: 'var(--muted)', textAlign: 'center' }}>No deals found</div>
                )}
              </div>

              {/* Orders */}
              <div style={{ borderBottom: '1px solid rgba(0,0,0,.08)' }}>
                <div style={{ padding: '12px 20px', background: 'var(--bg)', fontWeight: 600, fontSize: 13 }}>
                  Orders ({data?.orders?.length || 0})
                </div>
                {data?.orders?.length > 0 ? (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.orders.map(order => (
                      <div
                        key={order.id || order.shopifyId}
                        style={{
                          padding: '10px 20px',
                          borderBottom: '1px solid rgba(0,0,0,.04)',
                          cursor: 'pointer',
                          background: selectedItem?.type === 'order' && selectedItem?.id === (order.id || order.shopifyId) ? 'var(--bg)' : 'white'
                        }}
                        onClick={() => selectItem('order', order.shopifyId || order.id, order)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 500 }}>{order.id || order.name}</span>
                          <span style={{ fontWeight: 600 }}>£{parseFloat(order.total || 0).toFixed(2)}</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                          {order.platform || 'Shopify'} • {order.status || 'unfulfilled'} • {formatDate(order.created || order.createdAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 20, color: 'var(--muted)', textAlign: 'center' }}>No orders found</div>
                )}
              </div>

              {/* Activities */}
              <div>
                <div style={{ padding: '12px 20px', background: 'var(--bg)', fontWeight: 600, fontSize: 13 }}>
                  Activities ({data?.activities?.length || 0})
                </div>
                {data?.activities?.length > 0 ? (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.activities.map(activity => (
                      <div
                        key={activity.id}
                        style={{
                          padding: '10px 20px',
                          borderBottom: '1px solid rgba(0,0,0,.04)'
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{activity.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                          {activity.activity_type} • {formatDate(activity.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 20, color: 'var(--muted)', textAlign: 'center' }}>No activities found</div>
                )}
              </div>

              {/* Detail Panel */}
              {selectedItem && (
                <div style={{ borderTop: '2px solid var(--primary)', background: '#f8fafc', padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 14 }}>
                      {selectedItem.type === 'ticket' && 'Ticket Details'}
                      {selectedItem.type === 'deal' && 'Deal Details'}
                      {selectedItem.type === 'order' && 'Order Details'}
                    </h3>
                    <button className="btn btn-sm btn-ghost" onClick={() => { setSelectedItem(null); setItemDetails(null); }}>Close</button>
                  </div>

                  {loadingDetails ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <div className="loading-spinner" style={{ width: 24, height: 24 }} />
                    </div>
                  ) : (
                    <>
                      {selectedItem.type === 'ticket' && (
                        <div style={{ fontSize: 13 }}>
                          <p><strong>Subject:</strong> {(itemDetails || selectedItem.data).subject || '(No subject)'}</p>
                          <p><strong>Status:</strong> {(itemDetails || selectedItem.data).status}</p>
                          <p><strong>Priority:</strong> {(itemDetails || selectedItem.data).priority}</p>
                          <p><strong>Created:</strong> {formatDate((itemDetails || selectedItem.data).created_at)}</p>
                          {(itemDetails || selectedItem.data).resolved_at && <p><strong>Resolved:</strong> {formatDate((itemDetails || selectedItem.data).resolved_at)}</p>}
                          {itemDetails?.body_text && (
                            <div style={{ marginTop: 12, padding: 12, background: 'white', borderRadius: 6, maxHeight: 150, overflow: 'auto' }}>
                              <strong style={{ display: 'block', marginBottom: 8 }}>Message:</strong>
                              <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>{itemDetails.body_text}</pre>
                            </div>
                          )}
                        </div>
                      )}

                      {selectedItem.type === 'deal' && (
                        <div style={{ fontSize: 13 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                            <p style={{ margin: 0 }}><strong>Deal Name:</strong></p>
                            <p style={{ margin: 0 }}>{(itemDetails || selectedItem.data).dealname || selectedItem.data.name}</p>

                            <p style={{ margin: 0 }}><strong>Customer:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.customer_name || '-'}</p>

                            <p style={{ margin: 0 }}><strong>Email:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.customer_email || '-'}</p>

                            <p style={{ margin: 0 }}><strong>Stage:</strong></p>
                            <p style={{ margin: 0 }}>{stageLabels[(itemDetails || selectedItem.data).dealstage || selectedItem.data.stage] || (itemDetails || selectedItem.data).dealstage || selectedItem.data.stage}</p>

                            <p style={{ margin: 0 }}><strong>Amount:</strong></p>
                            <p style={{ margin: 0 }}>£{parseFloat((itemDetails || selectedItem.data).amount || 0).toFixed(2)}</p>

                            <p style={{ margin: 0 }}><strong>Created:</strong></p>
                            <p style={{ margin: 0 }}>{formatDate((itemDetails || selectedItem.data).created_at || selectedItem.data.createdAt)}</p>

                            {itemDetails?.carrier && (
                              <>
                                <p style={{ margin: 0 }}><strong>Carrier:</strong></p>
                                <p style={{ margin: 0 }}>{itemDetails.carrier}</p>
                              </>
                            )}

                            {itemDetails?.tracking_number && (
                              <>
                                <p style={{ margin: 0 }}><strong>Tracking:</strong></p>
                                <p style={{ margin: 0 }}>
                                  <a
                                    href={
                                      itemDetails.carrier?.toLowerCase().includes('fedex')
                                        ? `https://www.fedex.com/fedextrack/?trknbr=${itemDetails.tracking_number}`
                                        : itemDetails.carrier?.toLowerCase().includes('dhl')
                                        ? `https://www.dhl.com/gb-en/home/tracking.html?tracking-id=${itemDetails.tracking_number}`
                                        : itemDetails.carrier?.toLowerCase().includes('ups')
                                        ? `https://www.ups.com/track?tracknum=${itemDetails.tracking_number}`
                                        : `https://www.royalmail.com/track-your-item#/tracking-results/${itemDetails.tracking_number}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#2563eb', textDecoration: 'underline' }}
                                  >
                                    {itemDetails.tracking_number}
                                  </a>
                                </p>
                              </>
                            )}

                            {itemDetails?.collection_date && (
                              <>
                                <p style={{ margin: 0 }}><strong>Collection Date:</strong></p>
                                <p style={{ margin: 0 }}>{formatDate(itemDetails.collection_date)}</p>
                              </>
                            )}
                          </div>

                          {itemDetails?.items && itemDetails.items.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                              <strong>Items ({itemDetails.items.length}):</strong>
                              <div style={{ marginTop: 8, maxHeight: 150, overflow: 'auto', background: 'white', borderRadius: 6 }}>
                                {itemDetails.items.map((item, i) => (
                                  <div key={i} style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,.05)', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{item.title || item.name || `Item ${i + 1}`}</span>
                                    <span style={{ fontWeight: 500 }}>£{parseFloat(item.price || item.amount || 0).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div style={{ marginTop: 12 }}>
                            <a
                              href={`/deals/all?email=${encodeURIComponent(email)}`}
                              className="btn btn-sm btn-primary"
                            >
                              View in Deals
                            </a>
                          </div>
                        </div>
                      )}

                      {selectedItem.type === 'order' && (
                        <div style={{ fontSize: 13 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                            <p style={{ margin: 0 }}><strong>Order ID:</strong></p>
                            <p style={{ margin: 0 }}>{(itemDetails || selectedItem.data).name || (itemDetails || selectedItem.data).order_id || selectedItem.data.id}</p>

                            <p style={{ margin: 0 }}><strong>Customer:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.customer?.first_name} {itemDetails?.customer?.last_name || ''}{!itemDetails?.customer?.first_name && (selectedItem.data.buyer || '-')}</p>

                            <p style={{ margin: 0 }}><strong>Email:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.email || itemDetails?.customer?.email || '-'}</p>

                            <p style={{ margin: 0 }}><strong>Platform:</strong></p>
                            <p style={{ margin: 0 }}>{(itemDetails || selectedItem.data).platform || 'Shopify'}</p>

                            <p style={{ margin: 0 }}><strong>Total:</strong></p>
                            <p style={{ margin: 0 }}>£{parseFloat((itemDetails || selectedItem.data).total_price || (itemDetails || selectedItem.data).total || 0).toFixed(2)}</p>

                            <p style={{ margin: 0 }}><strong>Fulfillment:</strong></p>
                            <p style={{ margin: 0 }}>{(itemDetails || selectedItem.data).fulfillment_status || (itemDetails || selectedItem.data).status || 'unfulfilled'}</p>

                            <p style={{ margin: 0 }}><strong>Financial:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.financial_status || (itemDetails || selectedItem.data).financial || '-'}</p>

                            <p style={{ margin: 0 }}><strong>Created:</strong></p>
                            <p style={{ margin: 0 }}>{formatDate((itemDetails || selectedItem.data).created_at || selectedItem.data.created)}</p>

                            {/* Tracking info from fulfillments */}
                            {itemDetails?.fulfillments?.length > 0 && itemDetails.fulfillments[0]?.tracking_number && (
                              <>
                                <p style={{ margin: 0 }}><strong>Carrier:</strong></p>
                                <p style={{ margin: 0 }}>{itemDetails.fulfillments[0].tracking_company || '-'}</p>

                                <p style={{ margin: 0 }}><strong>Tracking:</strong></p>
                                <p style={{ margin: 0 }}>
                                  {itemDetails.fulfillments[0].tracking_url ? (
                                    <a
                                      href={itemDetails.fulfillments[0].tracking_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#2563eb', textDecoration: 'underline' }}
                                    >
                                      {itemDetails.fulfillments[0].tracking_number}
                                    </a>
                                  ) : (
                                    itemDetails.fulfillments[0].tracking_number
                                  )}
                                </p>
                              </>
                            )}
                          </div>

                          {itemDetails?.line_items && itemDetails.line_items.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                              <strong>Items ({itemDetails.line_items.length}):</strong>
                              <div style={{ marginTop: 8, maxHeight: 150, overflow: 'auto', background: 'white', borderRadius: 6 }}>
                                {itemDetails.line_items.map((item, i) => (
                                  <div key={i} style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,.05)', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{item.title || item.sku || `Item ${i + 1}`}</span>
                                    <span style={{ fontWeight: 500 }}>£{parseFloat(item.price || 0).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div style={{ marginTop: 12 }}>
                            <a
                              href={`/orders?order_id=${(itemDetails || selectedItem.data).name || selectedItem.data.id}`}
                              className="btn btn-sm btn-primary"
                            >
                              View in Orders
                            </a>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Tickets - Customer support inbox tailored for Audio Trader
 *
 * Features:
 * - Gmail sync with auto-filtering
 * - AI template matching for quick responses
 * - Auto-linking to HubSpot deals
 * - Customer history (previous tickets + deals)
 * - Internal notes (staff-only)
 * - Bulk actions for efficiency
 * - Priority auto-detection
 */
function Tickets() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [tickets, setTickets] = useState([])
  const [counts, setCounts] = useState({ new: 0, awaiting_reply: 0, waiting_customer: 0, waiting_third_party: 0, needs_reply: 0, waiting: 0, resolved: 0, spam: 0, service: 0, offers: 0 })
  const [lastSyncAt, setLastSyncAt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState(null)
  const [activeTab, setActiveTab] = useState('needs_reply')
  const [showMoreDropdown, setShowMoreDropdown] = useState(false)
  const moreDropdownRef = useRef(null)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [ticketDetails, setTicketDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [search, setSearch] = useState('')

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [bulkMode, setBulkMode] = useState(false)

  // Reply state
  const [replyText, setReplyText] = useState('')
  const [sending, setSending] = useState(false)
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [replyFile, setReplyFile] = useState(null)

  // Customer history
  const [customerHistory, setCustomerHistory] = useState(null)
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Order/Deal detail modals from side panel
  const [sidebarOrderModal, setSidebarOrderModal] = useState(null)
  const [sidebarDealModal, setSidebarDealModal] = useState(null)
  const [loadingSidebarOrder, setLoadingSidebarOrder] = useState(null)

  // Sidebar pin state
  const [pinnedItems, setPinnedItems] = useState({}) // "deal:123" => pinId
  const [togglingPin, setTogglingPin] = useState(null)

  // Internal notes
  const [notes, setNotes] = useState([])
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)

  // Case flow
  const [returnRequest, setReturnRequest] = useState(null)
  const [returnReasons, setReturnReasons] = useState([])
  const [showReturnForm, setShowReturnForm] = useState(false)
  const [returnFormData, setReturnFormData] = useState({ reason_id: '', reason_notes: '' })
  const [returnLoading, setReturnLoading] = useState(false)
  const [override30DayCase, setOverride30DayCase] = useState(false)

  // Filter modal
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [filters, setFilters] = useState([])
  const [filterSearch, setFilterSearch] = useState('')
  const [newFilter, setNewFilter] = useState({
    filter_type: 'email',
    pattern: '',
    match_type: 'exact',
    action: 'block',
    reason: '',
    subject_pattern: '',
    subject_match_type: 'contains'
  })
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Customer lookup modal
  const [customerEmail, setCustomerEmail] = useState(null)

  // Pin modal
  const [showPinModal, setShowPinModal] = useState(false)
  const [pinData, setPinData] = useState({ reason: '', dueDate: '', assignedTo: '' })
  const [pinUsers, setPinUsers] = useState([])

  // Signature modal
  const [showSignatureModal, setShowSignatureModal] = useState(false)
  const [signature, setSignature] = useState('')
  const [savingSignature, setSavingSignature] = useState(false)

  // Template management
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [templateForm, setTemplateForm] = useState({ name: '', body: '', keywords: '' })
  const [savingTemplate, setSavingTemplate] = useState(false)

  // Menu dropdown
  const [showMenuDropdown, setShowMenuDropdown] = useState(false)
  const menuDropdownRef = useRef(null)

  // Sync failures
  const [showSyncFailuresModal, setShowSyncFailuresModal] = useState(false)
  const [syncFailures, setSyncFailures] = useState([])
  const [syncFailureCounts, setSyncFailureCounts] = useState({ failed: 0, abandoned: 0, total: 0 })
  const [loadingSyncFailures, setLoadingSyncFailures] = useState(false)
  const [retryingSyncFailures, setRetryingSyncFailures] = useState(false)

  // Assignment
  const [assignmentFilter, setAssignmentFilter] = useState('all')
  const [ticketUsers, setTicketUsers] = useState([])

  // Close menu dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(e.target)) {
        setShowMenuDropdown(false)
      }
    }
    if (showMenuDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenuDropdown])

  // Close "More" dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(e.target)) {
        setShowMoreDropdown(false)
      }
    }
    if (showMoreDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMoreDropdown])

  // Responsive layout
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const [leftPanelWidth, setLeftPanelWidth] = useState(350)
  const [rightPanelWidth, setRightPanelWidth] = useState(280)
  const [quickRepliesHeight, setQuickRepliesHeight] = useState(320)
  const [mobileView, setMobileView] = useState('list') // 'list' | 'detail' | 'sidebar'
  const mainContainerRef = useRef(null)
  const [layoutHeight, setLayoutHeight] = useState(null)

  // HubSpot deal stage name mappings
  const stageLabels = {
    'appointmentscheduled': 'Quote Requested',
    '4383256790': 'Quote Ready',
    'qualifiedtobuy': 'Quote Sent',
    '2136864980': 'Warehouse Drop Off',
    '597442256': 'Awaiting Shipping Label',
    '4367683815': 'Awaiting Collection',
    'presentationscheduled': 'Shipping Arranged',
    '835164638': 'Arrived To Test',
    'contractsent': 'Awaiting Payment',
    'closedwon': 'Closed Traded',
    'closedlost': 'Closed Lost',
    '589630139': 'Quote Chased',
    '589142218': 'Problem With Shipping',
    '588059587': 'Issue with Item',
    '609520315': 'Listing Service Offered',
    '853396936': 'Listing Service Listing',
    '638945017': 'Last Chance Offer',
    '901659893': 'More Info Needed',
    '1616888039': 'Duplicate Trade Request',
    '4311086309': 'Artifacts'
  }

  useEffect(() => {
    loadTickets()
    loadTemplates()
    loadSyncStatus()
    // Load return reasons once
    if (returnReasons.length === 0) {
      api.get('/api/orders/returns/reasons').then(res => {
        setReturnReasons(res.reasons || [])
      }).catch(() => {})
    }
  }, [activeTab, search, assignmentFilter])

  useEffect(() => {
    loadTicketUsers()
  }, [])

  // Check for ticketId in URL to auto-open a specific ticket
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const ticketIdParam = params.get('ticketId')
    if (ticketIdParam) {
      loadTicketById(ticketIdParam)
      // Clean up URL
      navigate(location.pathname, { replace: true })
    }
  }, [location.search])

  async function loadTicketById(ticketId) {
    try {
      const response = await api.get(`/api/tickets/${ticketId}`)
      if (response.ticket) {
        setSelectedTicket(response.ticket)
        setTicketDetails(response)
        // Switch to the appropriate tab based on ticket status
        if (response.ticket.status) {
          const statusToTab = {
            'new': 'needs_reply',
            'awaiting_reply': 'needs_reply',
            'waiting_customer': 'waiting',
            'waiting_third_party': 'waiting',
            'resolved': 'resolved',
            'spam': 'spam',
            'service': 'service',
            'offers': 'offers'
          }
          setActiveTab(statusToTab[response.ticket.status] || 'needs_reply')
        }
        // Mark as read in Gmail when viewing
        api.post(`/api/tickets/${ticketId}/mark-read`).catch(err => {
          console.error('Failed to mark ticket as read in Gmail:', err)
        })
      }
    } catch (error) {
      console.error('Failed to load ticket:', error)
    }
  }

  // Window resize tracking for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Measure layout height — constrain ticketsLayout to remaining viewport space
  useEffect(() => {
    if (!mainContainerRef.current) return
    const top = mainContainerRef.current.getBoundingClientRect().top
    setLayoutHeight(window.innerHeight - top)
  })

  // Responsive breakpoints
  const isMobile = windowWidth < 768
  const isTablet = windowWidth >= 768 && windowWidth < 1024
  const isDesktop = windowWidth >= 1024

  // Handle left panel resize
  const handleLeftResize = useCallback((clientX) => {
    if (!mainContainerRef.current) return
    const containerRect = mainContainerRef.current.getBoundingClientRect()
    const newWidth = clientX - containerRect.left
    setLeftPanelWidth(Math.max(250, Math.min(500, newWidth)))
  }, [])

  // Handle right panel resize
  const handleRightResize = useCallback((clientX) => {
    if (!mainContainerRef.current) return
    const containerRect = mainContainerRef.current.getBoundingClientRect()
    const newWidth = containerRect.right - clientX
    setRightPanelWidth(Math.max(200, Math.min(400, newWidth)))
  }, [])

  // Handle quick replies resize
  const handleQuickRepliesResize = useCallback((clientY) => {
    const newHeight = window.innerHeight - clientY - 100 // Account for footer space
    setQuickRepliesHeight(Math.max(80, Math.min(300, newHeight)))
  }, [])

  async function loadTickets() {
    setLoading(true)
    try {
      const params = new URLSearchParams({ status: activeTab })
      if (search) params.set('search', search)
      if (assignmentFilter !== 'all') params.set('assigned_to', assignmentFilter)
      const response = await api.get(`/api/tickets?${params}`)
      setTickets(response.tickets || [])
      setCounts(response.counts || { new: 0, awaiting_reply: 0, waiting_customer: 0, waiting_third_party: 0, needs_reply: 0, waiting: 0, resolved: 0, spam: 0, service: 0, offers: 0 })
      if (response.lastSyncAt) setLastSyncAt(response.lastSyncAt)
    } catch (error) {
      console.error('Failed to load tickets:', error)
    } finally {
      setLoading(false)
    }
  }

  // Helper to determine ticket status indicators
  function getTicketIndicators(ticket) {
    const isNew = lastSyncAt && new Date(ticket.created_at) > new Date(lastSyncAt)
    const isUnviewed = !ticket.viewed_at
    return { isNew, isUnviewed }
  }

  function getUserInitials(userId) {
    const matched = ticketUsers.find(u => (u.user_id || u.id) === userId)
    if (!matched) return '?'
    return (matched.name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  function getUserName(userId) {
    const matched = ticketUsers.find(u => (u.user_id || u.id) === userId)
    return matched?.name || 'Unknown'
  }

  async function loadTemplates() {
    try {
      const response = await api.get('/api/tickets/templates')
      setTemplates(response.templates || [])
    } catch (error) {
      console.error('Failed to load templates:', error)
    }
  }

  async function loadFilters() {
    try {
      const response = await api.get('/api/tickets/filters')
      setFilters(response.filters || [])
    } catch (error) {
      console.error('Failed to load filters:', error)
    }
  }

  async function loadSyncStatus() {
    try {
      const response = await api.get('/api/tickets/sync-status')
      setSyncStatus(response)
    } catch (error) {
      // Ignore - sync status is optional info
    }
  }

  async function loadTicketUsers() {
    try {
      const response = await api.get('/api/tickets/assignees')
      setTicketUsers(response.users || [])
    } catch (error) {
      console.error('Failed to load ticket users:', error)
    }
  }

  async function loadSyncFailures() {
    setLoadingSyncFailures(true)
    try {
      const response = await api.get('/api/tickets/sync/failures')
      setSyncFailures(response.failures || [])
      setSyncFailureCounts(response.counts || { failed: 0, abandoned: 0, total: 0 })
    } catch (error) {
      console.error('Failed to load sync failures:', error)
    } finally {
      setLoadingSyncFailures(false)
    }
  }

  async function retrySyncFailures(messageIds = null) {
    setRetryingSyncFailures(true)
    try {
      const body = messageIds ? { messageIds } : { retryAll: true }
      const response = await api.post('/api/tickets/sync/retry', body)
      if (response.success) {
        alert(`${response.reset} message(s) queued for retry. Run sync to process them.`)
        loadSyncFailures()
        loadSyncStatus()
      }
    } catch (error) {
      alert('Failed to retry: ' + error.message)
    } finally {
      setRetryingSyncFailures(false)
    }
  }

  async function loadSignature() {
    try {
      const response = await api.get('/api/tickets/signature')
      setSignature(response.signature || '')
    } catch (error) {
      console.error('Failed to load signature:', error)
    }
  }

  async function saveSignature() {
    setSavingSignature(true)
    try {
      await api.post('/api/tickets/signature', { signature })
      setShowSignatureModal(false)
    } catch (error) {
      alert(`Failed to save signature: ${error.message}`)
    } finally {
      setSavingSignature(false)
    }
  }

  async function loadTemplates() {
    setLoadingTemplates(true)
    try {
      const response = await api.get('/api/tickets/templates')
      setTemplates(response.templates || [])
    } catch (error) {
      console.error('Failed to load templates:', error)
    } finally {
      setLoadingTemplates(false)
    }
  }

  async function saveTemplate() {
    if (!templateForm.name || !templateForm.body) {
      alert('Please enter a name and template body')
      return
    }
    setSavingTemplate(true)
    try {
      if (editingTemplate) {
        await api.patch(`/api/tickets/templates/${editingTemplate.id}`, templateForm)
      } else {
        await api.post('/api/tickets/templates', templateForm)
      }
      loadTemplates()
      setEditingTemplate(null)
      setTemplateForm({ name: '', body: '', keywords: '' })
    } catch (error) {
      alert(`Failed to save template: ${error.message}`)
    } finally {
      setSavingTemplate(false)
    }
  }

  async function deleteTemplate(id) {
    if (!confirm('Delete this template?')) return
    try {
      await api.delete(`/api/tickets/templates/${id}`)
      loadTemplates()
    } catch (error) {
      alert(`Failed to delete template: ${error.message}`)
    }
  }

  function editTemplate(template) {
    setEditingTemplate(template)
    setTemplateForm({
      name: template.name,
      body: template.body_text || '',
      keywords: template.keywords ? JSON.parse(template.keywords).join(', ') : ''
    })
  }

  function cancelTemplateEdit() {
    setEditingTemplate(null)
    setTemplateForm({ name: '', body: '', keywords: '' })
  }

  async function syncEmails(fullResync = false) {
    setSyncing(true)
    try {
      // If full re-sync requested, reset the sync state first
      if (fullResync) {
        await api.post('/api/tickets/sync/reset')
      }

      // Process in batches — backend handles all deduplication via ticket_sync_messages table
      let totalSynced = 0
      let totalThreaded = 0
      let totalFiltered = 0
      let totalSkipped = 0
      let allErrors = []
      let batchCount = 0
      let totalEmails = 0
      let totalNew = 0
      let isIncremental = false

      const MAX_BATCHES = 20
      while (true) {
        batchCount++
        if (batchCount > MAX_BATCHES) {
          console.warn(`Reached max batch limit (${MAX_BATCHES}), stopping sync`)
          break
        }
        setSyncStatus(prev => ({ ...prev, syncingBatch: batchCount }))

        const result = await api.post('/api/tickets/sync')

        if (!result.success) {
          throw new Error(result.error || 'Sync failed')
        }

        // Accumulate results
        totalSynced += result.synced || 0
        totalThreaded += result.threaded || 0
        totalFiltered += result.filtered || 0
        if (result.errors?.length) {
          allErrors.push(...result.errors)
        }

        // Capture totals from first batch
        if (batchCount === 1) {
          totalEmails = result.total || 0
          totalNew = result.totalNew || 0
          totalSkipped = result.skipped || 0
          isIncremental = result.isIncremental
        }

        if (!result.hasMore) break

        // Safety: if no successful progress was made in this batch, stop to avoid infinite loop
        const successfulProgress = (result.synced || 0) + (result.threaded || 0) + (result.filtered || 0)
        if (successfulProgress === 0) {
          console.warn('No successful progress in batch (only errors), stopping sync to avoid infinite retry')
          break
        }
      }

      // Build detailed breakdown
      const lines = [`Found ${totalEmails} emails (${isIncremental ? 'incremental' : 'full sync'})`]
      if (batchCount > 1) lines.push(`Processed in ${batchCount} batches`)
      lines.push('')
      lines.push(`✓ ${totalSynced} new tickets created`)
      lines.push(`↩ ${totalThreaded} threaded as replies`)
      lines.push(`⊘ ${totalFiltered} blocked by filters`)
      lines.push(`○ ${totalSkipped} already imported`)

      if (allErrors.length > 0) {
        lines.push(`✗ ${allErrors.length} errors (tracked for auto-retry)`)
        lines.push('')
        lines.push('Failed messages will retry automatically with backoff.')
        lines.push('Check sync failures in the menu for details.')
      }

      alert(lines.join('\n'))
      loadTickets()
      loadSyncStatus()
    } catch (error) {
      alert(`Sync failed: ${error.message}`)
    } finally {
      setSyncing(false)
      setSyncStatus(prev => ({ ...prev, syncingBatch: null }))
    }
  }

  // Open order detail modal from sidebar
  async function openSidebarOrder(order) {
    if (!order.shopifyId) return
    setLoadingSidebarOrder(order.shopifyId)
    try {
      const res = await api.get(`/api/orders/shopify/${order.shopifyId}`)
      if (res.order) {
        setSidebarOrderModal(res.order)
      }
    } catch (err) {
      console.error('Failed to load order:', err)
    } finally {
      setLoadingSidebarOrder(null)
    }
  }

  // Open deal detail modal from sidebar
  function openSidebarDeal(deal) {
    setSidebarDealModal({ id: deal.id, dealname: deal.name })
  }

  async function toggleSidebarPin(entityType, entityId) {
    const key = `${entityType}:${entityId}`
    const existingPinId = pinnedItems[key]
    setTogglingPin(key)

    if (existingPinId) {
      // Optimistic unpin
      setPinnedItems(prev => { const next = { ...prev }; delete next[key]; return next })
      try {
        await api.delete(`/api/pinned/${existingPinId}`)
      } catch (err) {
        setPinnedItems(prev => ({ ...prev, [key]: existingPinId }))
        console.error('Failed to unpin:', err)
      }
    } else {
      // Optimistic pin
      setPinnedItems(prev => ({ ...prev, [key]: 'pending' }))
      try {
        const res = await api.post('/api/pinned/quick', { entityType, entityId: String(entityId) })
        setPinnedItems(prev => ({ ...prev, [key]: res.id }))
      } catch (err) {
        setPinnedItems(prev => { const next = { ...prev }; delete next[key]; return next })
        console.error('Failed to pin:', err)
      }
    }
    setTogglingPin(null)
  }

  async function selectTicket(ticket) {
    if (bulkMode) {
      toggleSelection(ticket.id)
      return
    }

    setSelectedTicket(ticket)
    setMobileView('detail') // Switch to detail view on mobile
    setLoadingDetails(true)
    setReplyText('')
    setSelectedTemplate(null)
    setCustomerHistory(null)
    setPinnedItems({})
    setNotes([])
    setReturnRequest(null)
    setShowReturnForm(false)

    try {
      const [detailsRes, historyRes, notesRes] = await Promise.all([
        api.get(`/api/tickets/${ticket.id}`),
        api.get(`/api/tickets/${ticket.id}/history`),
        api.get(`/api/tickets/${ticket.id}/notes`)
      ])

      setTicketDetails(detailsRes)
      setCustomerHistory(historyRes)
      setNotes(notesRes.notes || [])

      // Build pin status map from history response
      const pinMap = {}
      if (historyRes.deals) {
        historyRes.deals.forEach(d => { if (d.pinned) pinMap[`deal:${d.id}`] = d.pinId })
      }
      if (historyRes.orders) {
        historyRes.orders.forEach(o => { if (o.pinned) pinMap[`order:${o.shopifyId || o.id}`] = o.pinId })
      }
      if (historyRes.previousTickets) {
        historyRes.previousTickets.forEach(t => { if (t.pinned) pinMap[`ticket:${t.id}`] = t.pinId })
      }
      setPinnedItems(pinMap)

      // Always check for existing case on this ticket
      api.get(`/api/orders/returns/by-ticket/${ticket.id}`).then(res => {
        setReturnRequest(res.returnRequest || null)
      }).catch(() => {})

      // Mark as read in Gmail when viewing
      if (ticket.status === 'new' || ticket.status === 'awaiting_reply' || !ticket.read_at) {
        api.post(`/api/tickets/${ticket.id}/mark-read`).catch(err => {
          console.error('Failed to mark ticket as read in Gmail:', err)
        })
      }
    } catch (error) {
      console.error('Failed to load ticket details:', error)
    } finally {
      setLoadingDetails(false)
    }
  }

  const [aiLoading, setAiLoading] = useState(false)
  const [rewriteLoading, setRewriteLoading] = useState(false)
  const [showRewriteModal, setShowRewriteModal] = useState(false)
  const [rewrittenText, setRewrittenText] = useState('')
  const [rewriteReported, setRewriteReported] = useState(false)
  const [rewriteContext, setRewriteContext] = useState('auto') // 'auto', 'trade_in', 'order'

  async function getSuggestion() {
    if (!selectedTicket) return

    setAiLoading(true)
    try {
      const response = await api.post(`/api/tickets/${selectedTicket.id}/suggest`)
      if (response.suggestedResponse) {
        setReplyText(response.suggestedResponse)
        if (response.template) {
          setSelectedTemplate(response.template.key)
        }
      } else {
        alert(response.message || 'Could not generate AI suggestion.')
      }
    } catch (error) {
      alert(`Failed to get suggestion: ${error.message}`)
    } finally {
      setAiLoading(false)
    }
  }

  async function getRewrite(contextOverride = null) {
    if (!selectedTicket || !replyText.trim()) {
      alert('Please enter some text to rewrite.')
      return
    }

    setRewriteLoading(true)
    try {
      const response = await api.post(`/api/tickets/${selectedTicket.id}/rewrite`, {
        text: replyText,
        contextType: contextOverride || rewriteContext
      })
      if (response.rewrittenText) {
        setRewrittenText(response.rewrittenText)
        setShowRewriteModal(true)
      } else {
        alert(response.message || 'Could not rewrite text.')
      }
    } catch (error) {
      alert(`Failed to rewrite: ${error.message}`)
    } finally {
      setRewriteLoading(false)
    }
  }

  function acceptRewrite() {
    setReplyText(rewrittenText)
    setShowRewriteModal(false)
    setRewrittenText('')
  }

  function cancelRewrite() {
    setShowRewriteModal(false)
    setRewrittenText('')
  }

  async function reportBadRewrite() {
    try {
      await api.post(`/api/tickets/${selectedTicket.id}/rewrite-feedback`, {
        originalDraft: replyText,
        rewrittenText,
        threadContext: null
      })
      setRewriteReported(true)
      setTimeout(() => {
        setShowRewriteModal(false)
        setRewrittenText('')
        setRewriteReported(false)
      }, 1000)
    } catch (error) {
      alert(`Failed to report: ${error.message}`)
    }
  }

  function useTemplate(template) {
    let text = template.body_text
    text = text.replace(/\{\{customer_name\}\}/g, selectedTicket?.from_name || 'Customer')
    setReplyText(text)
    setSelectedTemplate(template.key)
  }

  async function sendReply() {
    if (!replyText.trim() || !selectedTicket) return

    setSending(true)
    try {
      const formData = new FormData()
      formData.append('message', replyText)
      if (selectedTemplate) formData.append('templateKey', selectedTemplate)
      if (replyFile) formData.append('file', replyFile)

      const res = await fetch(`/api/tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || 'Failed to send reply')
      }
      alert('Reply sent successfully!')
      setReplyText('')
      setSelectedTemplate(null)
      setReplyFile(null)
      loadTickets()
      selectTicket(selectedTicket)
    } catch (error) {
      alert(`Failed to send: ${error.message}`)
    } finally {
      setSending(false)
    }
  }

  async function handleAssign(ticketId, userId) {
    try {
      await api.patch(`/api/tickets/${ticketId}`, { assigned_to: userId })
      loadTickets()
      if (ticketDetails?.ticket?.id === ticketId) {
        selectTicket({ id: ticketId })
      }
    } catch (error) {
      console.error('Failed to assign ticket:', error)
      alert(`Failed to assign: ${error.message}`)
    }
  }

  async function updateTicketStatus(ticketId, status) {
    try {
      await api.patch(`/api/tickets/${ticketId}`, { status })
      loadTickets()
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(null)
        setTicketDetails(null)
      }
    } catch (error) {
      alert(`Failed to update: ${error.message}`)
    }
  }

  // Bulk actions
  function toggleSelection(id) {
    const newSet = new Set(selectedIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedIds(newSet)
  }

  function selectAll() {
    if (selectedIds.size === tickets.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(tickets.map(t => t.id)))
    }
  }

  async function bulkUpdateStatus(status) {
    if (selectedIds.size === 0) return
    if (!confirm(`Mark ${selectedIds.size} ticket(s) as ${status}?`)) return

    try {
      await api.post('/api/tickets/bulk-status', {
        ticketIds: Array.from(selectedIds),
        status
      })
      setSelectedIds(new Set())
      setBulkMode(false)
      loadTickets()
    } catch (error) {
      alert(`Failed: ${error.message}`)
    }
  }

  // Quick ignore sender
  async function ignoreSender(type = 'email') {
    if (!selectedTicket) return
    if (!confirm(`Block all future emails from this ${type}?`)) return

    try {
      const result = await api.post(`/api/tickets/${selectedTicket.id}/ignore-sender`, { type })
      if (result.alreadyExists) {
        alert(`Filter already exists for: ${result.pattern}`)
      } else {
        alert(`Filter added for: ${result.pattern}`)
      }
      setSelectedTicket(null)
      setTicketDetails(null)
      loadTickets()
    } catch (error) {
      alert(`Failed: ${error.message}`)
    }
  }

  // Mark sender/domain as Service Email
  async function markAsService(type = 'email') {
    if (!selectedTicket) return
    if (!confirm(`Mark all future emails from this ${type} as Service Emails?`)) return

    try {
      const result = await api.post(`/api/tickets/${selectedTicket.id}/set-service`, { type })
      alert(`Service filter added for: ${result.pattern}`)
      // Also update the current ticket to service status
      await updateTicketStatus(selectedTicket.id, 'service')
      setSelectedTicket(null)
      setTicketDetails(null)
      loadTickets()
    } catch (error) {
      alert(`Failed: ${error.message}`)
    }
  }

  async function pinTicket() {
    if (!selectedTicket || !pinData.reason) return

    try {
      const body = {
        entityType: 'ticket',
        entityId: String(selectedTicket.id).replace('.0', ''),
        issueArea: 'Ticket',
        reason: pinData.reason,
        dueDate: pinData.dueDate || null
      }
      if (pinData.assignedTo) {
        body.assignedTo = Number(pinData.assignedTo)
      }
      const res = await api.post('/api/pinned', body)
      setPinnedItems(prev => ({ ...prev, [`ticket:${selectedTicket.id}`]: res.id || 'pinned' }))
      setShowPinModal(false)
      setPinData({ reason: '', dueDate: '', assignedTo: '' })
    } catch (error) {
      alert(`Failed to pin: ${error.message}`)
    }
  }

  // Notes
  async function addNote() {
    if (!newNote.trim() || !selectedTicket) return

    setSavingNote(true)
    try {
      await api.post(`/api/tickets/${selectedTicket.id}/notes`, { content: newNote })
      setNewNote('')
      const notesRes = await api.get(`/api/tickets/${selectedTicket.id}/notes`)
      setNotes(notesRes.notes || [])
    } catch (error) {
      alert(`Failed to add note: ${error.message}`)
    } finally {
      setSavingNote(false)
    }
  }

  // Case handlers
  async function createReturnRequest() {
    if (!selectedTicket || !returnFormData.reason_id) return
    setReturnLoading(true)
    try {
      const order = customerHistory?.orders?.[0]
      const createRes = await api.post('/api/orders/returns', {
        ticket_id: selectedTicket.id,
        reason_id: parseInt(returnFormData.reason_id),
        reason_notes: returnFormData.reason_notes || null,
        customer_description: ticketDetails?.ticket?.body_text || null,
        order_id: order?.shopifyId || null,
        order_name: order?.id || null,
        shopify_order_id: order?.shopifyId || null,
        product_title: order?.line_items?.[0]?.title || null,
        product_type: order?.line_items?.[0]?.product_type || null,
        order_date: order?.created || order?.createdAt || null,
        override_30_day: override30DayCase || false,
        source_name: order?.source_name || null,
        channel_conversation_id: order?.channel_conversation_id || null,
        reverb_order_id: order?.reverb_order_id || null
      })
      // Case-opened notification email is sent by the backend directly from no-reply@audiotrader.co.uk
      const returnRes = await api.get(`/api/orders/returns/by-ticket/${selectedTicket.id}`)
      setReturnRequest(returnRes.returnRequest || null)
      setShowReturnForm(false)
      setReturnFormData({ reason_id: '', reason_notes: '' })
    } catch (error) {
      alert(`Failed to open case: ${error.message}`)
    } finally {
      setReturnLoading(false)
    }
  }

  function getCaseStatusBadge(status) {
    const styles = {
      logged: { bg: '#dbeafe', color: '#1e40af', label: 'Open' },
      awaiting_evidence: { bg: '#fef3c7', color: '#92400e', label: 'Awaiting Evidence' },
      troubleshooting_sent: { bg: '#e0e7ff', color: '#3730a3', label: 'Troubleshooting' },
      return_approved: { bg: '#d1fae5', color: '#065f46', label: 'Return Approved' },
      refunded: { bg: '#d1fae5', color: '#065f46', label: 'Refunded' },
      closed: { bg: '#f3f4f6', color: '#6b7280', label: 'Closed' }
    }
    const s = styles[status] || { bg: '#f3f4f6', color: '#6b7280', label: status }
    return (
      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: s.bg, color: s.color }}>
        {s.label}
      </span>
    )
  }

  // Filters
  async function addFilter() {
    if (!newFilter.pattern) return

    try {
      const filterData = { ...newFilter }
      // Only include subject fields if they're set
      if (!filterData.subject_pattern) {
        delete filterData.subject_pattern
        delete filterData.subject_match_type
      }
      await api.post('/api/tickets/filters', filterData)
      setNewFilter({ filter_type: 'email', pattern: '', match_type: 'exact', action: 'block', reason: '', subject_pattern: '', subject_match_type: 'contains' })
      setShowAdvanced(false)
      loadFilters()
    } catch (error) {
      alert(`Failed to add filter: ${error.message}`)
    }
  }

  async function deleteFilter(id) {
    if (!confirm('Delete this filter?')) return

    try {
      await api.delete(`/api/tickets/filters/${id}`)
      loadFilters()
    } catch (error) {
      alert(`Failed to delete: ${error.message}`)
    }
  }

  async function applyFilterToExisting(id) {
    if (!confirm('Apply this filter to all existing tickets that match? This will update their status or delete them based on the filter action.')) return

    try {
      const result = await api.post(`/api/tickets/filters/${id}/apply`)
      if (result.deleted) {
        alert(`Deleted ${result.deleted} matching ticket(s)`)
      } else if (result.updated) {
        alert(`Updated ${result.updated} ticket(s) to ${result.newStatus}`)
      } else {
        alert(result.message || 'No matching tickets found')
      }
      loadTickets()
    } catch (error) {
      alert(`Failed to apply filter: ${error.message}`)
    }
  }

  // Group templates by category
  const templatesByCategory = templates.reduce((acc, t) => {
    const cat = t.category || 'general'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(t)
    return acc
  }, {})

  // Priority badge colors
  const priorityColors = {
    urgent: '#dc2626',
    high: '#ea580c',
    normal: '#6b7280',
    low: '#22c55e'
  }

  // Helper: derive ticket type from template_category
  function getTicketType(ticket) {
    const cat = ticket.template_category
    if (!cat) return null
    const labels = {
      'returns': 'Return',
      'shipping': 'Shipping',
      'order': 'Order',
      'trade_in': 'Trade-In',
      'general': 'General',
      'payment': 'Payment',
      'product': 'Product',
      'account': 'Account'
    }
    return labels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)
  }

  // Helper: map status to display label + CSS class
  function getStateDisplay(status) {
    const map = {
      'new': { label: 'New', className: 'ticket-row__state--new' },
      'awaiting_reply': { label: 'Awaiting reply', className: 'ticket-row__state--awaiting-reply' },
      'waiting_customer': { label: 'Waiting on customer', className: 'ticket-row__state--waiting-customer' },
      'waiting_third_party': { label: 'Waiting on 3rd party', className: 'ticket-row__state--waiting-third-party' },
      'resolved': { label: 'Resolved', className: 'ticket-row__state--resolved' },
      'spam': { label: 'Spam', className: '' },
      'service': { label: 'Service', className: '' },
      'offers': { label: 'Offer', className: '' }
    }
    return map[status] || { label: status, className: '' }
  }

  return (
    <>
    <div className="page page--tickets">
      <div className="pageHead">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-start',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 12 : 0
        }}>
          <div>
            <h1>Tickets</h1>
            {!isMobile && <p>Customer support inbox with AI-powered responses</p>}
          </div>
          <div style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'flex-start' : 'flex-end'
          }}>
            {syncStatus?.lastSyncAt && !isMobile && (
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                Last sync: {formatDate(syncStatus.lastSyncAt)}
                {syncStatus.hasHistoryId && ' (incremental)'}
              </span>
            )}
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                className={`btn ${isMobile ? 'btn-sm' : ''} btn-primary`}
                onClick={() => syncEmails(false)}
                disabled={syncing}
              >
                {syncing ? (syncStatus?.syncingBatch > 1 ? `Syncing (batch ${syncStatus.syncingBatch})...` : 'Syncing...') : 'Sync'}
              </button>
              {(syncStatus?.failedMessages > 0 || syncStatus?.abandonedMessages > 0) && (
                <span
                  onClick={(e) => { e.stopPropagation(); loadSyncFailures(); setShowSyncFailuresModal(true); }}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#DC2626',
                    color: 'white',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    lineHeight: 1
                  }}
                  title="Sync failures - click to view"
                >
                  {(syncStatus.failedMessages || 0) + (syncStatus.abandonedMessages || 0)}
                </span>
              )}
            </div>
            {/* Menu dropdown */}
            <div ref={menuDropdownRef} style={{ position: 'relative' }}>
              <button
                className={`btn ${isMobile ? 'btn-sm' : ''} btn-secondary`}
                onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                title="More options"
                style={{ padding: '8px 10px' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              {showMenuDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 4,
                  background: 'white',
                  border: '1px solid var(--stroke)',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  minWidth: 160,
                  zIndex: 100,
                  overflow: 'hidden'
                }}>
                  <button
                    onClick={() => { setShowFilterModal(true); setFilterSearch(''); loadFilters(); setShowMenuDropdown(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    🔍 Filters
                  </button>
                  <button
                    onClick={() => { loadSignature(); setShowSignatureModal(true); setShowMenuDropdown(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    ✍️ Signature
                  </button>
                  <button
                    onClick={() => { loadTemplates(); setShowTemplateModal(true); setShowMenuDropdown(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    📝 Templates
                  </button>
                  <button
                    onClick={() => { loadSyncFailures(); setShowSyncFailuresModal(true); setShowMenuDropdown(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    ⚠️ Sync Failures
                    {(syncStatus?.failedMessages > 0 || syncStatus?.abandonedMessages > 0) && (
                      <span style={{
                        background: '#DC2626',
                        color: 'white',
                        borderRadius: 10,
                        padding: '1px 6px',
                        fontSize: 11,
                        fontWeight: 600,
                        marginLeft: 'auto'
                      }}>
                        {(syncStatus.failedMessages || 0) + (syncStatus.abandonedMessages || 0)}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => { syncEmails(true); setShowMenuDropdown(false); }}
                    disabled={syncing}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left',
                      color: '#DC2626'
                    }}
                    onMouseEnter={e => e.target.style.background = '#fef2f2'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    🔄 Full Re-Sync
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-input"
          placeholder={isMobile ? "Search..." : "Search by email, subject, or name..."}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: isMobile ? '100%' : 400, width: '100%', flex: '1 1 auto' }}
        />
      </div>

      {/* Assignment Filter (admin only) + Status Tabs */}
      {user?.is_admin && (
        <div style={{ marginBottom: 6 }}>
          <select
            className="form-input"
            value={assignmentFilter}
            onChange={e => setAssignmentFilter(e.target.value)}
            style={{ width: 'auto', minWidth: 150, fontSize: 13, padding: '4px 8px' }}
            aria-label="Filter tickets by assignment"
          >
            <option value="all">All Tickets</option>
            <option value="me">My Tickets</option>
            <option value="unassigned">Unassigned</option>
          </select>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div
          className="pageTabs"
          style={{
            overflowX: isMobile ? 'auto' : 'visible',
            whiteSpace: isMobile ? 'nowrap' : 'normal',
            paddingBottom: isMobile ? 8 : 0,
            WebkitOverflowScrolling: 'touch',
            flex: 1
          }}
        >
          <button
            className={activeTab === 'needs_reply' ? 'active' : ''}
            onClick={() => setActiveTab('needs_reply')}
            style={{ fontSize: isMobile ? 12 : undefined, position: 'relative' }}
          >
            {(counts.needs_reply || 0) > 0 && <span style={{ display: 'inline-block', width: 8, height: 8, background: '#dc2626', borderRadius: '50%', marginRight: 6 }} />}
            Needs Reply ({counts.needs_reply || 0})
          </button>
          <button
            className={activeTab === 'waiting' ? 'active' : ''}
            onClick={() => setActiveTab('waiting')}
            style={{ fontSize: isMobile ? 12 : undefined }}
          >
            Waiting ({counts.waiting || 0})
          </button>
          <button
            className={activeTab === 'resolved' ? 'active' : ''}
            onClick={() => setActiveTab('resolved')}
            style={{ fontSize: isMobile ? 12 : undefined }}
          >
            Resolved ({counts.resolved || 0})
          </button>
          {/* More dropdown for secondary tabs */}
          <div ref={moreDropdownRef} className="ticket-tabs__more" style={{ display: 'inline-block' }}>
            <button
              className={['offers', 'service', 'spam'].includes(activeTab) ? 'active' : ''}
              onClick={() => setShowMoreDropdown(!showMoreDropdown)}
              style={{ fontSize: isMobile ? 12 : undefined }}
            >
              {activeTab === 'offers' ? `Offers (${counts.offers || 0})` :
               activeTab === 'service' ? `Service (${counts.service || 0})` :
               activeTab === 'spam' ? `Spam (${counts.spam || 0})` :
               'More'}
              <span style={{ marginLeft: 4, fontSize: 10 }}>&#9662;</span>
            </button>
            {showMoreDropdown && (
              <div className="ticket-tabs__dropdown">
                <button
                  className={`ticket-tabs__dropdown-item ${activeTab === 'offers' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('offers'); setShowMoreDropdown(false); }}
                >
                  Offers <span className="ticket-tabs__dropdown-count">{counts.offers || 0}</span>
                </button>
                <button
                  className={`ticket-tabs__dropdown-item ${activeTab === 'service' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('service'); setShowMoreDropdown(false); }}
                >
                  Service <span className="ticket-tabs__dropdown-count">{counts.service || 0}</span>
                </button>
                <button
                  className={`ticket-tabs__dropdown-item ${activeTab === 'spam' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('spam'); setShowMoreDropdown(false); }}
                >
                  Spam <span className="ticket-tabs__dropdown-count">{counts.spam || 0}</span>
                </button>
              </div>
            )}
          </div>
        </div>
        <button
          className={`btn ${isMobile ? 'btn-sm' : ''} ${bulkMode ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); }}
          style={{ flexShrink: 0 }}
        >
          {bulkMode ? 'Cancel Bulk' : 'Bulk Edit'}
        </button>
      </div>

      {/* Bulk Actions Bar */}
      {bulkMode && selectedIds.size > 0 && (
        <div style={{
          display: 'flex',
          gap: 12,
          padding: 12,
          background: 'var(--bg)',
          borderRadius: 8,
          marginTop: 16,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 600 }}>{selectedIds.size} selected</span>
          <button className="btn btn-sm btn-success" onClick={() => bulkUpdateStatus('resolved')}>
            Mark Resolved
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('waiting_customer')}>
            Waiting on Customer
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('waiting_third_party')}>
            Waiting on 3rd Party
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('service')}>
            Mark Service
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('spam')}>
            Mark Spam
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('new')}>
            Reopen
          </button>
        </div>
      )}

      {/* Mobile View Toggle */}
      {isMobile && selectedTicket && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, background: 'var(--bg)', padding: 8, borderRadius: 8 }}>
          <button
            className={`btn btn-sm ${mobileView === 'list' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setMobileView('list')}
          >
            List
          </button>
          <button
            className={`btn btn-sm ${mobileView === 'detail' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setMobileView('detail')}
          >
            Detail
          </button>
          <button
            className={`btn btn-sm ${mobileView === 'sidebar' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setMobileView('sidebar')}
          >
            Info
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="ticketsPage" style={{ marginTop: 18 }}>

      <div
        ref={mainContainerRef}
        className="ticketsLayout"
        style={{
          display: isMobile ? 'block' : 'flex',
          height: layoutHeight && !isMobile ? layoutHeight : undefined
        }}
      >
        {/* Ticket List */}
        <aside
          className="card ticketListColumn"
          style={{
            padding: 0,
            width: isMobile ? '100%' : (selectedTicket ? leftPanelWidth : '100%'),
            display: isMobile && selectedTicket && mobileView !== 'list' ? 'none' : 'flex'
          }}
        >
          {bulkMode && tickets.length > 0 && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', background: 'var(--bg)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.size === tickets.length && tickets.length > 0}
                  onChange={selectAll}
                />
                Select All
              </label>
            </div>
          )}

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className="loading-spinner" />
            </div>
          ) : tickets.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
              <p>No {activeTab} tickets</p>
            </div>
          ) : (
            <div>
              {tickets.map(ticket => {
                const isMuted = ['spam', 'service', 'offers'].includes(ticket.status)
                const stateDisplay = getStateDisplay(ticket.status)
                const ticketType = getTicketType(ticket)
                return (
                  <div
                    key={ticket.id}
                    className={`ticket-row${selectedTicket?.id === ticket.id ? ' ticket-row--selected' : ''}${selectedIds.has(ticket.id) ? ' ticket-row--bulk-selected' : ''}${isMuted ? ' ticket-row--muted' : ''}`}
                    onClick={() => selectTicket(ticket)}
                  >
                    {bulkMode && (
                      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 12 }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(ticket.id)}
                          onChange={() => toggleSelection(ticket.id)}
                          onClick={e => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {/* Priority bar */}
                    <div className={`ticket-row__priority-bar ticket-row__priority-bar--${ticket.priority || 'normal'}`} />

                    {/* Body */}
                    <div className="ticket-row__body">
                      <div className={`ticket-row__subject${!ticket.viewed_at ? ' ticket-row__subject--unread' : ''}`}>
                        {ticket.subject || '(No subject)'}
                        {ticket.channel === 'ebay' && (
                          <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#0654ba', color: 'white', verticalAlign: 'middle' }}>eBay</span>
                        )}
                        {ticket.channel === 'reverb' && (
                          <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#6b21a8', color: 'white', verticalAlign: 'middle' }}>Reverb</span>
                        )}
                      </div>
                      <div className="ticket-row__sender">
                        <a
                          onClick={(e) => { e.stopPropagation(); setCustomerEmail(ticket.from_email || ticket.customer_email); }}
                          title="View customer history"
                        >
                          {ticket.customer_name_extracted || ticket.from_name || ticket.from_email || ticket.customer_email}
                        </a>
                        {ticket.customer_email && (
                          <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 6 }} title={`Via ${ticket.from_email}`}>
                            (via contact form)
                          </span>
                        )}
                      </div>
                      <div className="ticket-row__meta">
                        {ticketType && (
                          <>
                            <span className="ticket-row__type">{ticketType}</span>
                            <span className="ticket-row__meta-dot" />
                          </>
                        )}
                        <span className={`ticket-row__state ${stateDisplay.className}`}>
                          {stateDisplay.label}
                        </span>
                      </div>
                      <div className="ticket-row__snippet">
                        {ticket.snippet}
                      </div>
                    </div>

                    {/* Right column */}
                    <div className="ticket-row__right">
                      {ticket.assigned_to ? (
                        <span
                          title={getUserName(ticket.assigned_to)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 24, height: 24, borderRadius: '50%',
                            background: 'var(--color-primary, #2563eb)', color: '#fff',
                            fontSize: 10, fontWeight: 600, flexShrink: 0
                          }}
                        >
                          {getUserInitials(ticket.assigned_to)}
                        </span>
                      ) : (
                        <span
                          title="Assign to me"
                          onClick={e => { e.stopPropagation(); handleAssign(ticket.id, user?.user_id) }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 24, height: 24, borderRadius: '50%',
                            border: '1.5px dashed var(--color-border, #d1d5db)', color: 'var(--color-text-muted, #9ca3af)',
                            fontSize: 10, cursor: 'pointer', flexShrink: 0
                          }}
                        >
                          +
                        </span>
                      )}
                      {!ticket.viewed_at && <div className="ticket-row__unread-dot" title="Not yet viewed" />}
                      <span className="ticket-row__date">{formatDate(ticket.created_at)}</span>
                      <div className="ticket-row__badges">
                        {ticket.deal_id && (
                          <span className="badge badge-success" style={{ fontSize: 10 }}>Linked</span>
                        )}
                        {ticket.order_id && (
                          <span className="badge badge-warning" style={{ fontSize: 10 }}>#{ticket.order_id}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </aside>

        {/* Resize Handle - Left */}
        {selectedTicket && !isMobile && (
          <ResizeHandle onResize={handleLeftResize} direction="horizontal" />
        )}

        {/* Detail wrapper: assignment bar + conversation + reply */}
        {selectedTicket && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

            {/* Assignment Bar */}
            {ticketDetails && !isMobile && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                marginBottom: 8,
                background: 'var(--bg)',
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,.08)',
                fontSize: 13
              }}>
                {/* Left: Subject + last updated */}
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {ticketDetails.ticket.subject || 'No subject'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    Updated {formatDate(ticketDetails.ticket.updated_at || ticketDetails.ticket.created_at)}
                  </div>
                </div>

                {/* Center: Assigned to */}
                <div style={{ flex: '0 0 auto', textAlign: 'center', padding: '0 24px' }}>
                  <span style={{ color: 'var(--muted)', fontWeight: 500 }}>Assigned to: </span>
                  {ticketDetails.ticket.assigned_to ? (
                    <span style={{ fontWeight: 600 }}>
                      {getUserName(ticketDetails.ticket.assigned_to)}
                      {ticketDetails.ticket.assigned_to === user?.user_id && (
                        <span style={{ color: 'var(--color-primary, #2563eb)', marginLeft: 4 }}>(you)</span>
                      )}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>Unassigned</span>
                  )}
                </div>

                {/* Right: Reassign dropdown */}
                <div style={{ flex: '1 1 0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                  <label htmlFor="reassign-ticket" style={{ color: 'var(--muted)', fontWeight: 500, whiteSpace: 'nowrap' }}>Assign to:</label>
                  <select
                    id="reassign-ticket"
                    className="form-input"
                    value={ticketDetails.ticket.assigned_to || ''}
                    onChange={e => handleAssign(selectedTicket.id, e.target.value ? parseInt(e.target.value, 10) : null)}
                    style={{ width: 'auto', minWidth: 140, fontSize: 12 }}
                    aria-label="Assign ticket to user"
                  >
                    <option value="">Unassigned</option>
                    {ticketUsers.map(u => (
                      <option key={u.user_id || u.id} value={u.user_id || u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>

        {/* Message Thread */}
        {(
          <section
            className="card conversationColumn"
            style={{
              padding: 0,
              display: isMobile && mobileView !== 'detail' ? 'none' : 'flex'
            }}
          >
            {loadingDetails ? (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <div className="loading-spinner" />
              </div>
            ) : ticketDetails && (
              <>
                {/* Mobile back button */}
                {isMobile && (
                  <div style={{ padding: '8px 16px', borderBottom: '1px solid rgba(0,0,0,.06)' }}>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => setMobileView('list')}
                      style={{ padding: '4px 8px' }}
                    >
                      ← Back to list
                    </button>
                  </div>
                )}

                <div className="messageList" style={{ background: 'var(--bg)' }}>
                  {/* Replies (newest first) */}
                  {[...(ticketDetails.replies || [])].reverse().map(reply => (
                    <div
                      key={reply.id}
                      style={{
                        background: reply.direction === 'out' ? '#e8f5e9' : 'white',
                        padding: 16,
                        borderRadius: 8,
                        marginBottom: 16,
                        marginLeft: reply.direction === 'out' ? 32 : 0,
                        marginRight: reply.direction === 'in' ? 32 : 0
                      }}
                    >
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                        {reply.direction === 'out' ? 'You' : ticketDetails.ticket.from_name} - {formatDate(reply.sent_at)}
                        {reply.template_used && (
                          <span style={{ marginLeft: 8, fontStyle: 'italic' }}>
                            (Template: {reply.template_used})
                          </span>
                        )}
                      </div>
                      <EmailContent
                        html={reply.body_html}
                        text={reply.body_text}
                      />
                    </div>
                  ))}

                  {/* Original message (oldest, at bottom) */}
                  <div style={{ background: 'white', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                      {formatDate(ticketDetails.ticket.created_at)}
                    </div>
                    <EmailContent
                      html={ticketDetails.ticket.body_html}
                      text={ticketDetails.ticket.body_text}
                      fallback={ticketDetails.ticket.snippet}
                    />
                    {/* Attachments */}
                    {ticketDetails.ticket.attachments && (() => {
                      const attachments = typeof ticketDetails.ticket.attachments === 'string'
                        ? JSON.parse(ticketDetails.ticket.attachments)
                        : ticketDetails.ticket.attachments
                      return attachments.length > 0 && (
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,.1)' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                            Attachments ({attachments.length})
                            {attachments.filter(a => a.attachmentId).length > 1 && (
                              <DownloadAllAttachments ticketId={ticketDetails.ticket.id} attachments={attachments} />
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {attachments.map((att, idx) => (
                              <AttachmentThumbnail
                                key={idx}
                                ticketId={ticketDetails.ticket.id}
                                attachment={att}
                              />
                            ))}
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </>
            )}
          </section>
        )}

        {/* Reply Column */}
        {selectedTicket && ticketDetails && (
          <section
            className="card replyColumn"
            style={{
              display: isMobile && mobileView !== 'detail' ? 'none' : undefined
            }}
          >
            <div className="quickReplySection">
              <div className="quickReplyHeader">
                <div className="quickReplyTitle">Quick Replies</div>
                <div className="quickReplyButtons">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={getSuggestion}
                    disabled={aiLoading}
                    title="Get AI-suggested response based on customer history and email content"
                  >
                    {aiLoading ? '🤖 Thinking...' : '🤖 AI Suggest'}
                  </button>
                  <div style={{ display: 'flex', gap: 0 }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => getRewrite()}
                      disabled={rewriteLoading || !replyText.trim()}
                      title="Rewrite your draft to be more professional and polished"
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    >
                      {rewriteLoading ? '✨ Rewriting...' : '✨ AI Rewrite'}
                    </button>
                    <select
                      value={rewriteContext}
                      onChange={e => setRewriteContext(e.target.value)}
                      className="form-input"
                      style={{
                        padding: '4px 6px',
                        fontSize: 11,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: 'none',
                        width: 'auto',
                        minWidth: 80
                      }}
                      title="Select context for AI rewrite"
                    >
                      <option value="auto">Auto</option>
                      <option value="trade_in">Trade-In</option>
                      <option value="order">Order</option>
                    </select>
                  </div>
                </div>
              </div>

              {selectedTicket?.channel === 'ebay' && (
                <div style={{ padding: '6px 10px', borderRadius: 4, background: '#EBF5FF', border: '1px solid #0654ba33', fontSize: 12, color: '#0654ba', marginBottom: 6 }}>
                  Reply will also be sent via eBay messaging{selectedTicket.channel_buyer_username ? ` to ${selectedTicket.channel_buyer_username}` : ''}
                  {!selectedTicket.channel_item_id && <span style={{ color: '#B91C1C', marginLeft: 6 }}>(item ID missing — eBay send may fail)</span>}
                </div>
              )}
              {selectedTicket?.channel === 'reverb' && (
                <div style={{ padding: '6px 10px', borderRadius: 4, background: '#F3E8FF', border: '1px solid #6b21a833', fontSize: 12, color: '#6b21a8', marginBottom: 6 }}>
                  Reply will also be sent via Reverb messaging{selectedTicket.channel_buyer_username ? ` to ${selectedTicket.channel_buyer_username}` : ''}
                </div>
              )}
              <textarea
                className="form-input replyTextarea"
                placeholder="Type your reply..."
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, marginBottom: 4 }}>
                <label
                  className="btn btn-sm btn-secondary"
                  style={{ cursor: 'pointer', margin: 0 }}
                >
                  Attach File
                  <input
                    type="file"
                    style={{ display: 'none' }}
                    onChange={e => setReplyFile(e.target.files[0] || null)}
                  />
                </label>
                {replyFile && (
                  <span style={{ fontSize: 12, color: '#374151' }}>
                    {replyFile.name}
                    <button
                      onClick={() => setReplyFile(null)}
                      style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontWeight: 'bold', padding: 0 }}
                    >
                      &times;
                    </button>
                  </span>
                )}
              </div>

              <div className="replyActions">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                  >
                    Resolve
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setShowPinModal(true)
                      setPinData({ reason: '', dueDate: '', assignedTo: '' })
                      if (user?.is_admin) {
                        api.get('/api/users').then(res => {
                          setPinUsers(Array.isArray(res) ? res : (res.users || res.data || []))
                        }).catch(err => console.error('Failed to load users:', err))
                      }
                    }}
                    title="Pin this ticket for follow-up"
                  >
                    Pin
                  </button>
                  <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,.15)', margin: '0 4px' }} />
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => markAsService('email')}
                    title="Route future emails from this address to Service"
                  >
                    Service (Email)
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => markAsService('domain')}
                    title="Route future emails from this domain to Service"
                  >
                    Service (Domain)
                  </button>
                  <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,.15)', margin: '0 4px' }} />
                  <button
                    className="btn btn-sm"
                    style={{ background: '#EF4444', color: 'white' }}
                    onClick={() => ignoreSender('email')}
                    title="Never import emails from this address"
                  >
                    Block Email
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ background: '#EF4444', color: 'white' }}
                    onClick={() => ignoreSender('domain')}
                    title="Never import emails from this domain"
                  >
                    Block Domain
                  </button>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={sendReply}
                  disabled={!replyText.trim() || sending}
                >
                  {sending ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            </div>
          </section>
        )}

            </div>
          </div>
        )}

        {/* Resize Handle - Right */}
        {selectedTicket && ticketDetails && !isMobile && (
          <ResizeHandle onResize={handleRightResize} direction="horizontal" />
        )}

        {/* Right Sidebar - Customer History, Tickets & Notes */}
        {selectedTicket && ticketDetails && (
          <aside
            className="rightSidebarColumn"
            style={{
              display: isMobile && mobileView !== 'sidebar' ? 'none' : 'flex',
              width: isMobile ? '100%' : rightPanelWidth
            }}
          >
            {/* Pinned Items */}
            {customerHistory && Object.keys(pinnedItems).length > 0 ? (
              <div className="card" style={{ padding: 0, borderLeft: '3px solid #f59e0b' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, color: '#92400e' }}>
                  <span style={{ fontSize: 14 }}>{'\uD83D\uDCCC'}</span>
                  <span>Pinned ({Object.keys(pinnedItems).length})</span>
                </div>
                <div style={{ padding: 8, maxHeight: 180, overflowY: 'auto' }}>
                  {customerHistory.deals?.filter(d => pinnedItems[`deal:${d.id}`]).map(d => (
                    <div
                      key={`pin-deal-${d.id}`}
                      style={{ fontSize: 12, marginBottom: 4, padding: '6px 8px', background: '#fffbeb', borderRadius: 4, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onClick={() => openSidebarDeal(d)}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || 'Unnamed Deal'}</div>
                        <div style={{ fontSize: 10, color: '#92400e' }}>Deal &middot; {stageLabels[d.stage] || d.stage}</div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSidebarPin('deal', d.id) }}
                        title="Unpin"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 13, color: '#dc2626' }}
                      >{'\uD83D\uDCCC'}</button>
                    </div>
                  ))}
                  {customerHistory.orders?.filter(o => pinnedItems[`order:${o.shopifyId || o.id}`]).map(o => {
                    const displayId = o.id?.startsWith('#') ? o.id : `#${o.id}`
                    return (
                      <div
                        key={`pin-order-${o.id}`}
                        style={{ fontSize: 12, marginBottom: 4, padding: '6px 8px', background: '#fffbeb', borderRadius: 4, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        onClick={() => openSidebarOrder(o)}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500 }}>{displayId} &middot; £{parseFloat(o.total || 0).toFixed(2)}</div>
                          <div style={{ fontSize: 10, color: '#92400e' }}>Order &middot; {o.platform}</div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleSidebarPin('order', o.shopifyId || o.id) }}
                          title="Unpin"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 13, color: '#dc2626' }}
                        >{'\uD83D\uDCCC'}</button>
                      </div>
                    )
                  })}
                  {customerHistory.previousTickets?.filter(t => pinnedItems[`ticket:${t.id}`]).map(t => (
                    <div
                      key={`pin-ticket-${t.id}`}
                      style={{ fontSize: 12, marginBottom: 4, padding: '6px 8px', background: '#fffbeb', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject || 'No subject'}</div>
                        <div style={{ fontSize: 10, color: '#92400e' }}>Ticket &middot; {t.status}</div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSidebarPin('ticket', t.id) }}
                        title="Unpin"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 13, color: '#dc2626' }}
                      >{'\uD83D\uDCCC'}</button>
                    </div>
                  ))}
                </div>
              </div>
            ) : customerHistory ? (
              <div className="card" style={{ padding: 0, opacity: 0.6 }}>
                <div style={{ padding: '10px 16px', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--muted)' }}>
                  <span style={{ fontSize: 14 }}>{'\uD83D\uDCCC'}</span>
                  <span>Pinned</span>
                </div>
                <div style={{ padding: '8px 16px 12px', fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>No pinned items</div>
              </div>
            ) : null}

            {/* Customer History (Deals & Orders) */}
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Customer History</span>
                <button
                  className="btn btn-sm"
                  style={{ color: '#2563eb', background: 'transparent', border: 'none', fontSize: 11, padding: '4px 8px' }}
                  onClick={() => setCustomerEmail(ticketDetails.ticket?.from_email || ticketDetails.ticket?.customer_email)}
                  title="View full customer profile"
                >
                  View All
                </button>
              </div>
              <div style={{ padding: 12 }}>
                {loadingHistory ? (
                  <div className="loading-spinner" style={{ margin: '16px auto', width: 20, height: 20 }} />
                ) : customerHistory ? (
                  <>
                    {/* Deals */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase' }}>
                        Deals ({customerHistory.deals?.length || 0})
                      </div>
                      {customerHistory.deals?.length > 0 ? (
                        <>
                          {customerHistory.deals.slice(0, 5).map(d => (
                            <div
                              key={d.id}
                              style={{ fontSize: 12, marginBottom: 6, padding: 8, background: 'var(--bg)', borderRadius: 4, cursor: 'pointer' }}
                              onClick={() => openSidebarDeal(d)}
                            >
                              <div style={{ fontWeight: 500, marginBottom: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || 'Unnamed Deal'}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleSidebarPin('deal', d.id) }}
                                  disabled={togglingPin === `deal:${d.id}`}
                                  title={pinnedItems[`deal:${d.id}`] ? 'Unpin deal' : 'Pin deal'}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 14, lineHeight: 1, opacity: pinnedItems[`deal:${d.id}`] ? 1 : 0.3, color: pinnedItems[`deal:${d.id}`] ? '#dc2626' : 'var(--muted)', transition: 'opacity 0.15s' }}
                                >{'\uD83D\uDCCC'}</button>
                              </div>
                              <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                                <span>{stageLabels[d.stage] || d.stage}</span>
                                {d.amount && <span style={{ fontWeight: 600 }}>£{parseFloat(d.amount).toFixed(2)}</span>}
                              </div>
                            </div>
                          ))}
                          {customerHistory.deals.length > 5 && (
                            <button
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: 11,
                                color: '#2563eb',
                                background: 'transparent',
                                border: '1px dashed #e5e7eb',
                                borderRadius: 4,
                                cursor: 'pointer',
                                marginTop: 4
                              }}
                              onClick={() => window.open(`/deals/all?email=${encodeURIComponent(ticketDetails?.ticket?.from_email || ticketDetails?.ticket?.customer_email || '')}`, '_blank')}
                            >
                              View all {customerHistory.deals.length} deals...
                            </button>
                          )}
                        </>
                      ) : (
                        <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8, textAlign: 'center' }}>No deals found</div>
                      )}
                    </div>

                    {/* Orders */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase' }}>
                        Orders ({customerHistory.orders?.length || 0})
                      </div>
                      {customerHistory.orders?.length > 0 ? (
                        <>
                          {customerHistory.orders.slice(0, 5).map(o => {
                            const displayId = o.id?.startsWith('#') ? o.id : `#${o.id}`
                            const orderEntityId = o.shopifyId || o.id
                            return (
                              <div
                                key={o.id}
                                style={{ fontSize: 12, marginBottom: 6, padding: 8, background: 'var(--bg)', borderRadius: 4, cursor: 'pointer', opacity: loadingSidebarOrder === o.shopifyId ? 0.6 : 1 }}
                                onClick={() => openSidebarOrder(o)}
                              >
                                <div style={{ fontWeight: 500, marginBottom: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>{displayId}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ fontWeight: 600 }}>£{parseFloat(o.total || 0).toFixed(2)}</span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleSidebarPin('order', orderEntityId) }}
                                      disabled={togglingPin === `order:${orderEntityId}`}
                                      title={pinnedItems[`order:${orderEntityId}`] ? 'Unpin order' : 'Pin order'}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 14, lineHeight: 1, opacity: pinnedItems[`order:${orderEntityId}`] ? 1 : 0.3, color: pinnedItems[`order:${orderEntityId}`] ? '#dc2626' : 'var(--muted)', transition: 'opacity 0.15s' }}
                                    >{'\uD83D\uDCCC'}</button>
                                  </div>
                                </div>
                                <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                                  <span>{o.platform}</span>
                                  <span>{loadingSidebarOrder === o.shopifyId ? 'Loading...' : o.status}</span>
                                </div>
                              </div>
                            )
                          })}
                          {customerHistory.orders.length > 5 && (
                            <button
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: 11,
                                color: '#2563eb',
                                background: 'transparent',
                                border: '1px dashed #e5e7eb',
                                borderRadius: 4,
                                cursor: 'pointer',
                                marginTop: 4
                              }}
                              onClick={() => window.open(`/orders?email=${encodeURIComponent(ticketDetails?.ticket?.from_email || ticketDetails?.ticket?.customer_email || '')}`, '_blank')}
                            >
                              View all {customerHistory.orders.length} orders...
                            </button>
                          )}
                        </>
                      ) : (
                        <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8, textAlign: 'center' }}>No orders found</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8, textAlign: 'center' }}>No history available</div>
                )}
              </div>
            </div>

            {/* Customer Tickets */}
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600, fontSize: 13 }}>
                Customer Tickets ({customerHistory?.previousTickets?.length || 0})
              </div>
              <div style={{ padding: 12, maxHeight: 200, overflowY: 'auto' }}>
                {loadingHistory ? (
                  <div className="loading-spinner" style={{ margin: '16px auto', width: 20, height: 20 }} />
                ) : customerHistory?.previousTickets?.length > 0 ? (
                  customerHistory.previousTickets.map(t => (
                    <div
                      key={t.id}
                      style={{ fontSize: 12, marginBottom: 6, padding: 8, background: 'var(--bg)', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => selectTicket(t)}
                    >
                      <div style={{ fontWeight: 500, marginBottom: 4, lineHeight: 1.3 }}>{t.subject || '(No subject)'}</div>
                      <div style={{ color: 'var(--muted)', display: 'flex', gap: 8, alignItems: 'center', fontSize: 11 }}>
                        <span className={`badge badge-${t.status === 'resolved' ? 'success' : (t.status === 'new' || t.status === 'awaiting_reply') ? 'warning' : 'secondary'}`} style={{ fontSize: 9, padding: '2px 6px' }}>
                          {getStateDisplay(t.status).label}
                        </span>
                        <span>{formatDate(t.created_at)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8, textAlign: 'center' }}>No previous tickets</div>
                )}
              </div>
            </div>

            {/* Case */}
            {ticketDetails?.ticket && (
              <div className="card" style={{ padding: 0 }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#dc2626' }}>&#9888;</span> Case
                  </span>
                  {returnRequest && getCaseStatusBadge(returnRequest.status)}
                </div>
                <div style={{ padding: 12 }}>
                  {/* No case yet - show Open Case button */}
                  {!returnRequest && !showReturnForm && (
                    <button
                      className="btn btn-sm"
                      style={{ width: '100%', background: '#dc2626', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                      onClick={() => setShowReturnForm(true)}
                    >
                      Open Case
                    </button>
                  )}

                  {/* Case form - pick reason + description */}
                  {!returnRequest && showReturnForm && (
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>What is the reason?</label>
                        <select
                          className="form-input"
                          value={returnFormData.reason_id}
                          onChange={e => setReturnFormData(prev => ({ ...prev, reason_id: e.target.value }))}
                          style={{ fontSize: 12, width: '100%' }}
                        >
                          <option value="">Select reason...</option>
                          {(() => {
                            const cancellationNames = ['Pre-Shipment Cancellation']
                            const generalNames = ['Changed My Mind', 'Not What I Expected', '30-Day Money Back Guarantee', 'Ordered By Mistake']
                            const cancellations = returnReasons.filter(r => cancellationNames.includes(r.name))
                            const general = returnReasons.filter(r => !r.requires_troubleshooting && generalNames.includes(r.name))
                            const nonTechFault = returnReasons.filter(r => !r.requires_troubleshooting && !generalNames.includes(r.name) && !cancellationNames.includes(r.name))
                            const technical = returnReasons.filter(r => r.requires_troubleshooting)
                            return (
                              <>
                                <optgroup label="Cancellations">
                                  {cancellations.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="General Returns (30-day guarantee)">
                                  {general.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="Non-Technical Issues">
                                  {nonTechFault.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="Technical Issues">
                                  {technical.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                  ))}
                                </optgroup>
                              </>
                            )
                          })()}
                        </select>
                      </div>
                      {returnFormData.reason_id && (() => {
                        const selectedReason = returnReasons.find(r => r.id === parseInt(returnFormData.reason_id));
                        if (!selectedReason) return null;
                        const isTechnical = selectedReason.requires_troubleshooting;
                        const cancellationNames = ['Pre-Shipment Cancellation']
                        const generalNames = ['Changed My Mind', 'Not What I Expected', '30-Day Money Back Guarantee', 'Ordered By Mistake']
                        const isCancellation = cancellationNames.includes(selectedReason.name)
                        const isGeneral = !isTechnical && !isCancellation && generalNames.includes(selectedReason.name)
                        const bg = isCancellation ? '#fef3c7' : isTechnical ? '#eff6ff' : isGeneral ? '#fefce8' : '#f0fdf4'
                        const border = isCancellation ? '#f59e0b' : isTechnical ? '#bfdbfe' : isGeneral ? '#fde68a' : '#bbf7d0'
                        const color = isCancellation ? '#92400e' : isTechnical ? '#1e40af' : isGeneral ? '#854d0e' : '#166534'
                        const label = isCancellation ? 'Pre-Shipment Cancellation' : isTechnical ? 'Technical Issue' : isGeneral ? '30-Day Guarantee Return' : 'Non-Technical Issue'
                        const desc = isCancellation
                          ? 'Case opened \u2192 Reviewer will cancel order and process refund'
                          : isTechnical
                            ? 'Case opened \u2192 Reviewer may request evidence & troubleshooting'
                            : 'Case opened \u2192 Reviewer will assess and decide next steps'
                        return (
                          <div style={{
                            margin: '0 0 8px', padding: '8px 12px', borderRadius: 6, fontSize: 11, lineHeight: 1.5,
                            background: bg, border: `1px solid ${border}`, color
                          }}>
                            <div style={{ fontWeight: 700, marginBottom: 2 }}>{label}</div>
                            <div>{desc}</div>
                          </div>
                        );
                      })()}
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Description</label>
                        <textarea
                          className="form-input"
                          placeholder="Describe the issue..."
                          value={returnFormData.reason_notes}
                          onChange={e => setReturnFormData(prev => ({ ...prev, reason_notes: e.target.value }))}
                          rows={3}
                          style={{ fontSize: 12, width: '100%' }}
                        />
                      </div>
                      {(() => {
                        const order = customerHistory?.orders?.[0]
                        const orderDateStr = order?.created || order?.createdAt
                        if (!orderDateStr) return null
                        const daysSince = Math.floor((Date.now() - new Date(orderDateStr).getTime()) / (1000 * 60 * 60 * 24))
                        if (daysSince <= 30) return null
                        return (
                          <div style={{ padding: '8px 10px', marginBottom: 8, borderRadius: 6, background: '#fef3c7', border: '1px solid #fde68a' }}>
                            <div style={{ fontSize: 11, color: '#92400e', lineHeight: 1.4, marginBottom: 6 }}>
                              <strong>Outside 30-day guarantee.</strong> This order was placed {daysSince} days ago. Returns and refunds are not available by default.
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                              <input
                                type="checkbox"
                                checked={override30DayCase}
                                onChange={e => setOverride30DayCase(e.target.checked)}
                                style={{ width: 14, height: 14 }}
                              />
                              <span style={{ fontWeight: 600, color: '#92400e' }}>Override — proceed anyway</span>
                            </label>
                          </div>
                        )
                      })()}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-sm"
                          style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', fontWeight: 600, fontSize: 11 }}
                          onClick={createReturnRequest}
                          disabled={!returnFormData.reason_id || !returnFormData.reason_notes?.trim() || returnLoading || (() => {
                            const order = customerHistory?.orders?.[0]
                            const orderDateStr = order?.created || order?.createdAt
                            if (!orderDateStr) return false
                            const daysSince = Math.floor((Date.now() - new Date(orderDateStr).getTime()) / (1000 * 60 * 60 * 24))
                            return daysSince > 30 && !override30DayCase
                          })()}
                        >
                          {returnLoading ? 'Submitting...' : 'Submit'}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          style={{ fontSize: 11 }}
                          onClick={() => { setShowReturnForm(false); setReturnFormData({ reason_id: '', reason_notes: '' }); setOverride30DayCase(false) }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Case exists - show See Case button */}
                  {returnRequest && (
                    <div>
                      <button
                        className="btn btn-sm"
                        style={{ width: '100%', fontSize: 12, background: '#2563eb', color: '#fff', border: 'none', fontWeight: 600, padding: '8px 12px', borderRadius: 6 }}
                        onClick={() => navigate(`/orders/cases?caseId=${returnRequest.id}`)}
                      >
                        See Case {returnRequest.public_id || '#' + returnRequest.id}
                      </button>
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>
                        {returnRequest.reason_name} &middot; {formatDate(returnRequest.created_at)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Internal Notes */}
            <div className="card" style={{ padding: 0, flex: 1 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600 }}>
                Internal Notes
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ marginBottom: 12 }}>
                  <textarea
                    className="form-input"
                    placeholder="Add a note (staff only)..."
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    rows={2}
                    style={{ marginBottom: 8, fontSize: 12 }}
                  />
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={addNote}
                    disabled={!newNote.trim() || savingNote}
                    style={{ width: '100%' }}
                  >
                    {savingNote ? 'Saving...' : 'Add Note'}
                  </button>
                </div>

                {notes.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 16 }}>
                    No notes yet
                  </div>
                ) : (
                  <div>
                    {notes.map(note => (
                      <div key={note.id} style={{ fontSize: 12, marginBottom: 12, padding: 8, background: '#fff3cd', borderRadius: 4 }}>
                        <div style={{ marginBottom: 4 }}>{note.content}</div>
                        <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                          {note.author_name || 'Staff'} • {formatDate(note.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>
        )}
      </div>
      </main>

      {/* Email Filters Modal */}
      {showFilterModal && (
        <div className="modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div className="modal-header">
              <h2>Email Filters</h2>
              <button className="modal-close" onClick={() => setShowFilterModal(false)}>&times;</button>
            </div>
            <div className="modal-body-scroll" style={{ maxHeight: 400 }}>
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                Configure rules to block emails or route them to specific streams.
              </p>

              {/* Add Filter Form */}
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 100px 120px 1fr auto', gap: 8, marginBottom: 20, alignItems: 'end' }}>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--muted)' }}>Type</label>
                  <select
                    className="form-input"
                    value={newFilter.filter_type}
                    onChange={e => setNewFilter({ ...newFilter, filter_type: e.target.value })}
                  >
                    <option value="email">Email</option>
                    <option value="domain">Domain</option>
                    <option value="subject">Subject</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--muted)' }}>
                    Pattern {newFilter.filter_type === 'subject' && '(contains)'}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={
                      newFilter.filter_type === 'domain' ? 'noreply.com' :
                      newFilter.filter_type === 'subject' ? 'shipping confirmation' :
                      'example@email.com'
                    }
                    value={newFilter.pattern}
                    onChange={e => setNewFilter({ ...newFilter, pattern: e.target.value })}
                  />
                </div>
                {newFilter.filter_type !== 'subject' && (
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--muted)' }}>Match</label>
                    <select
                      className="form-input"
                      value={newFilter.match_type}
                      onChange={e => setNewFilter({ ...newFilter, match_type: e.target.value })}
                    >
                      <option value="exact">Exact</option>
                      <option value="contains">Contains</option>
                      <option value="starts_with">Starts with</option>
                      <option value="ends_with">Ends with</option>
                    </select>
                  </div>
                )}
                {newFilter.filter_type === 'subject' && <div />}
                <div>
                  <label style={{ fontSize: 12, color: 'var(--muted)' }}>Action</label>
                  <select
                    className="form-input"
                    value={newFilter.action}
                    onChange={e => setNewFilter({ ...newFilter, action: e.target.value })}
                  >
                    <option value="block">Block (don't import)</option>
                    <option value="status:service">Set as Service</option>
                    <option value="status:offers">Set as Offers</option>
                    <option value="status:spam">Set as Spam</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--muted)' }}>Reason</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Optional note"
                    value={newFilter.reason}
                    onChange={e => setNewFilter({ ...newFilter, reason: e.target.value })}
                  />
                </div>
                <button className="btn btn-primary" onClick={addFilter}>Add</button>
              </div>

              {/* Compound Filter - Subject Condition (for Email/Domain filters) */}
              {newFilter.filter_type !== 'subject' && (
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0369a1', marginBottom: 8 }}>
                    + Optional: Add Subject Condition
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                    If set, both {newFilter.filter_type} AND subject must match
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8 }}>
                    <div>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Subject contains... (leave blank for any)"
                        value={newFilter.subject_pattern}
                        onChange={e => setNewFilter({ ...newFilter, subject_pattern: e.target.value })}
                        style={{ fontSize: 12 }}
                      />
                    </div>
                    <div>
                      <select
                        className="form-input"
                        value={newFilter.subject_match_type}
                        onChange={e => setNewFilter({ ...newFilter, subject_match_type: e.target.value })}
                        style={{ fontSize: 12 }}
                      >
                        <option value="contains">Contains</option>
                        <option value="exact">Exact</option>
                        <option value="starts_with">Starts with</option>
                        <option value="ends_with">Ends with</option>
                      </select>
                    </div>
                  </div>
                  {newFilter.subject_pattern && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#059669', fontWeight: 500 }}>
                      Filter: {newFilter.filter_type} "{newFilter.pattern}" + subject {newFilter.subject_match_type} "{newFilter.subject_pattern}"
                    </div>
                  )}
                </div>
              )}

              {/* Filter List */}
              {filters.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>
                  No filters configured
                </div>
              ) : (
                <>
                  {/* Search input */}
                  <div style={{ marginBottom: 12 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search filters by pattern, reason, or type..."
                      value={filterSearch}
                      onChange={e => setFilterSearch(e.target.value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                  {(() => {
                    const searchLower = filterSearch.toLowerCase()
                    const filteredFilters = filters.filter(f =>
                      !filterSearch ||
                      f.pattern?.toLowerCase().includes(searchLower) ||
                      f.reason?.toLowerCase().includes(searchLower) ||
                      f.filter_type?.toLowerCase().includes(searchLower) ||
                      f.action?.toLowerCase().includes(searchLower) ||
                      f.subject_pattern?.toLowerCase().includes(searchLower)
                    )
                    return filteredFilters.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>
                        No filters match "{filterSearch}"
                      </div>
                    ) : (
                      <table style={{ width: '100%', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid rgba(0,0,0,.1)' }}>
                            <th style={{ textAlign: 'left', padding: 8 }}>Type</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>Pattern</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>Match</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>Action</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>Reason</th>
                            <th style={{ textAlign: 'right', padding: 8 }}>Hits</th>
                            <th style={{ padding: 8 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredFilters.map(filter => (
                            <tr key={filter.id} style={{ borderBottom: '1px solid rgba(0,0,0,.06)' }}>
                              <td style={{ padding: 8 }}>
                                <span className="badge badge-secondary">{filter.filter_type}</span>
                                {filter.subject_pattern && (
                                  <span className="badge" style={{ marginLeft: 4, background: '#8B5CF6', color: 'white' }}>+subject</span>
                                )}
                              </td>
                              <td style={{ padding: 8, fontFamily: 'monospace' }}>
                                {filter.pattern}
                                {filter.subject_pattern && (
                                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                                    + subject {filter.subject_match_type || 'contains'}: "{filter.subject_pattern}"
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: 8 }}>{filter.filter_type === 'subject' ? 'contains' : filter.match_type}</td>
                              <td style={{ padding: 8 }}>
                                {filter.action === 'block' ? (
                                  <span className="badge" style={{ background: '#EF4444', color: 'white' }}>Block</span>
                                ) : filter.action === 'status:service' ? (
                                  <span className="badge" style={{ background: '#6B7280', color: 'white' }}>→ Service</span>
                                ) : filter.action === 'status:offers' ? (
                                  <span className="badge" style={{ background: '#8B5CF6', color: 'white' }}>→ Offers</span>
                                ) : filter.action === 'status:spam' ? (
                                  <span className="badge" style={{ background: '#F59E0B', color: 'white' }}>→ Spam</span>
                                ) : filter.action?.startsWith('status:') ? (
                                  <span className="badge" style={{ background: '#6B7280', color: 'white' }}>
                                    → {filter.action.replace('status:', '')}
                                  </span>
                                ) : (
                                  <span className="badge badge-secondary">{filter.action || 'block'}</span>
                                )}
                              </td>
                              <td style={{ padding: 8, color: 'var(--muted)' }}>{filter.reason || '-'}</td>
                              <td style={{ padding: 8, textAlign: 'right' }}>{filter.hits}</td>
                              <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => applyFilterToExisting(filter.id)}
                                  title="Apply this filter to existing tickets"
                                  style={{ marginRight: 4 }}
                                >
                                  Apply
                                </button>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => deleteFilter(filter.id)}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  })()}
                  {filterSearch && (
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
                      Showing {filters.filter(f =>
                        f.pattern?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                        f.reason?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                        f.filter_type?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                        f.action?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                        f.subject_pattern?.toLowerCase().includes(filterSearch.toLowerCase())
                      ).length} of {filters.length} filters
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowFilterModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Lookup Modal */}
      {customerEmail && (
        <CustomerModal
          email={customerEmail}
          onClose={() => setCustomerEmail(null)}
        />
      )}

      {/* Pin Ticket Modal */}
      {showPinModal && selectedTicket && (
        <div className="modalOverlay" onClick={() => setShowPinModal(false)}>
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 450 }}>
            <div className="modalHead">
              <h2>Pin Ticket</h2>
              <button className="modalClose" onClick={() => setShowPinModal(false)}>&times;</button>
            </div>
            <div className="modalBody">
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                Pin this ticket for follow-up. It will appear in your pinned items list.
              </p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  Why are you pinning this? *
                </label>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="e.g., Waiting for customer response, Need to follow up on refund..."
                  value={pinData.reason}
                  onChange={e => setPinData({ ...pinData, reason: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  Due Date (optional)
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={pinData.dueDate}
                  onChange={e => setPinData({ ...pinData, dueDate: e.target.value })}
                />
              </div>
              {user?.is_admin && pinUsers.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                    Assign To
                  </label>
                  <select
                    className="form-input"
                    value={pinData.assignedTo}
                    onChange={e => setPinData({ ...pinData, assignedTo: e.target.value })}
                  >
                    <option value="">Myself (default)</option>
                    {pinUsers.map(u => (
                      <option key={u.user_id} value={u.user_id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={() => setShowPinModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={pinTicket}
                disabled={!pinData.reason.trim()}
              >
                Pin Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Management Modal */}
      {showTemplateModal && (
        <div className="modalOverlay" onClick={() => setShowTemplateModal(false)}>
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '90vh' }}>
            <div className="modalHead">
              <h2>Reply Templates</h2>
              <button className="modalClose" onClick={() => setShowTemplateModal(false)}>&times;</button>
            </div>
            <div className="modalBody" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                Create templates for common replies. AI will suggest these when it detects matching messages.
              </p>

              {/* Template Form */}
              <div style={{
                background: 'var(--bg-alt)',
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
                border: '1px solid var(--border)'
              }}>
                <h4 style={{ margin: '0 0 12px 0' }}>
                  {editingTemplate ? 'Edit Template' : 'New Template'}
                </h4>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                    Template Name
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Drop Off Hours"
                    value={templateForm.name}
                    onChange={e => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                    Keywords (comma separated)
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. drop off, opening hours, visit, warehouse"
                    value={templateForm.keywords}
                    onChange={e => setTemplateForm(prev => ({ ...prev, keywords: e.target.value }))}
                  />
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                    AI uses these to match incoming messages to this template
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                    Template Body
                  </label>
                  <textarea
                    className="form-input"
                    rows={5}
                    placeholder="Enter your template text here..."
                    value={templateForm.body}
                    onChange={e => setTemplateForm(prev => ({ ...prev, body: e.target.value }))}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {editingTemplate && (
                    <button className="btn btn-secondary btn-sm" onClick={cancelTemplateEdit}>
                      Cancel Edit
                    </button>
                  )}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={saveTemplate}
                    disabled={savingTemplate}
                  >
                    {savingTemplate ? 'Saving...' : editingTemplate ? 'Update Template' : 'Add Template'}
                  </button>
                </div>
              </div>

              {/* Existing Templates */}
              <h4 style={{ margin: '0 0 12px 0' }}>Existing Templates</h4>
              {loadingTemplates ? (
                <div style={{ textAlign: 'center', padding: 20 }}>Loading...</div>
              ) : templates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>
                  No templates yet. Create one above.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {templates.map(template => (
                    <div
                      key={template.id}
                      style={{
                        padding: 12,
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: 8
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                          <strong>{template.name}</strong>
                          {template.keywords && (
                            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                              Keywords: {JSON.parse(template.keywords || '[]').join(', ')}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="btn btn-xs btn-secondary"
                            onClick={() => editTemplate(template)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-xs btn-secondary"
                            onClick={() => deleteTemplate(template.id)}
                            style={{ color: '#DC2626' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div style={{
                        fontSize: 13,
                        color: '#666',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 80,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {template.body_text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={() => setShowTemplateModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="modalOverlay" onClick={() => setShowSignatureModal(false)}>
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modalHead">
              <h2>Email Signature</h2>
              <button className="modalClose" onClick={() => setShowSignatureModal(false)}>&times;</button>
            </div>
            <div className="modalBody">
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                This signature will be automatically appended to all ticket replies.
              </p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  Signature
                </label>
                <textarea
                  className="form-input"
                  rows={6}
                  placeholder="Enter your signature here...&#10;&#10;Example:&#10;Best regards,&#10;Your Name&#10;your@email.com"
                  value={signature}
                  onChange={e => setSignature(e.target.value)}
                  style={{ fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                Tip: Use line breaks to format your signature. HTML is not supported.
              </div>
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={() => setShowSignatureModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveSignature}
                disabled={savingSignature}
              >
                {savingSignature ? 'Saving...' : 'Save Signature'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Failures Modal */}
      {showSyncFailuresModal && (
        <div className="modalOverlay" onClick={() => setShowSyncFailuresModal(false)}>
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modalHead">
              <h2>Sync Failures</h2>
              <button className="modalClose" onClick={() => setShowSyncFailuresModal(false)}>&times;</button>
            </div>
            <div className="modalBody">
              {loadingSyncFailures ? (
                <p>Loading...</p>
              ) : syncFailures.length === 0 ? (
                <p style={{ color: 'var(--muted)' }}>No sync failures. All messages processed successfully.</p>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    {syncFailureCounts.failed > 0 && (
                      <span style={{ background: '#FEF3C7', color: '#92400E', padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 500 }}>
                        {syncFailureCounts.failed} failed (will retry)
                      </span>
                    )}
                    {syncFailureCounts.abandoned > 0 && (
                      <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 500 }}>
                        {syncFailureCounts.abandoned} abandoned (max retries reached)
                      </span>
                    )}
                  </div>
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {syncFailures.map((f, i) => (
                      <div key={f.gmail_message_id} style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--stroke)',
                        fontSize: 13
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {f.gmail_message_id}
                            </div>
                            {f.error_message && (
                              <div style={{ color: '#DC2626', marginBottom: 4 }}>{f.error_message}</div>
                            )}
                            <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                              Retries: {f.retry_count}/{f.max_retries}
                              {f.next_retry_at && f.status === 'failed' && ` · Next retry: ${formatDate(f.next_retry_at)}`}
                              {f.status === 'abandoned' && ' · Abandoned'}
                            </div>
                          </div>
                          {f.status === 'abandoned' && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => retrySyncFailures([f.gmail_message_id])}
                              disabled={retryingSyncFailures}
                              style={{ flexShrink: 0 }}
                            >
                              Retry
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={() => setShowSyncFailuresModal(false)}>
                Close
              </button>
              {syncFailures.length > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={() => retrySyncFailures()}
                  disabled={retryingSyncFailures}
                >
                  {retryingSyncFailures ? 'Retrying...' : 'Retry All'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Rewrite Modal */}
      {showRewriteModal && (
        <div className="modalOverlay" onClick={cancelRewrite}>
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modalHead">
              <h2>AI Rewritten Message</h2>
              <button className="modalClose" onClick={cancelRewrite}>&times;</button>
            </div>
            <div className="modalBody">
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                Here's a polished version of your message. Click Accept to use it, or Cancel to keep your original.
              </p>
              <div style={{
                background: 'var(--bg-alt)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 16,
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                lineHeight: 1.6,
                maxHeight: 400,
                overflowY: 'auto'
              }}>
                {rewrittenText}
              </div>
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={cancelRewrite}>
                Cancel
              </button>
              <button
                className="btn btn-secondary"
                onClick={reportBadRewrite}
                disabled={rewriteReported}
                style={{ color: rewriteReported ? 'var(--muted)' : 'var(--danger, #e74c3c)' }}
              >
                {rewriteReported ? 'Reported' : 'Report Bad Response'}
              </button>
              <button className="btn btn-primary" onClick={acceptRewrite}>
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

      {/* Order detail modal opened from sidebar */}
      {sidebarOrderModal && (
        <OrderDetailModal
          order={sidebarOrderModal}
          onClose={() => setSidebarOrderModal(null)}
        />
      )}

      {/* Deal detail modal opened from sidebar */}
      {sidebarDealModal && (
        <DealDetailModal
          deal={sidebarDealModal}
          onClose={() => setSidebarDealModal(null)}
        />
      )}
    </>
  )
}

export default Tickets
